import {
	Inline,
	Box,
	Button,
	Icons,
	Stack,
	Divider,
	Table,
	TableHeaderDataCell,
	TableDataCell,
	Text,
	TableFooterDataCell,
	Tooltip,
	useDidMount,
	Money,
	Heading,
	Alert,
	DeferRender,
	Grid,
	Col,
	Component,
	SelectInput,
} from "@sembark-travel/ui-base"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	TextAreaInputField,
	TextInputField,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	useFieldValue,
	useForm,
	GetFieldValue,
	SwitchInputField,
	PercentageInputField,
	isTruthy,
	SelectInputField,
	useCurrencyPairRatioFieldValue,
	FieldArray,
	arrayMutators,
	InlineSelectInputField,
	addKeyToFieldArrayItem,
	getFieldArrayItemKey,
	MoneyInputField,
	MarkdownInputField,
} from "@sembark-travel/ui-form"
import {
	normalizeNumber,
	numberToLocalString,
} from "@sembark-travel/number-utils"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useCallback, useEffect, useMemo, useState } from "react"
import * as Validator from "yup"
import config from "../config"
import {
	useTripRefresh,
	IGivenQuote,
	ITripQuote,
	TPricePerPersonAllocation,
	TGivenQuotePricingStrategy,
	TPricePerTravelComponentAllocation,
	IQuoteComponentsCostPricesDistributed,
	TTaxAppliedOn,
	TComponentKey,
	TTaxPerComponentAllocation,
	partitionPerPersonAllocations,
} from "./store"
import {
	addMoney,
	convertMoneyToCurrency,
	createMoney,
	currencyPairsArrayToObject,
	currencyPairsObjectToArray,
	currencyPairToHuman,
	divideMoneyBy,
	formatMoneyByDecimal,
	formatMoneyByIntl,
	getPercentageOfMoney,
	makeCurrencyConverter,
	moneyParseByDecimal,
	roundMoney,
	roundMoneyToPoint,
	subtractMoney,
	TCurrency,
	TCurrencyPair,
	TCurrencyPairsObject,
	TMoney,
} from "@sembark-travel/money"
import {
	CurrencyPairInputFieldWithRates,
	useCurrenciesOfTenant,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import {
	calculateMarginFromTotalPrice,
	calculateMarginPercentageFromTotalPrice,
	determineFinalPricingStrategy,
	prepareGivenPricesPerComponentPayloadDispatcher,
	prepareGlobalTaxAppliedOnPayloadDispatcher,
	prepareMarginsPayloadForOptionDispatcher,
	preparePerComponentTaxAllocationPayloadDispatcher,
	prepareTaxEditingTotalPayloadForOptionDispatcher,
	prepareTaxPercentagePayloadForOptionDispatcher,
	prepareUnroundedGivenPricesPerComponentPayloadDispatcher,
	usePricingStrategyMigrationSync,
	groupPricesPerPersonByService,
} from "./utils"
import { getTotalPriceForExtras } from "../utils"
import { Required } from "utility-types"
import { InlineSelectTaxTypeInputField } from "../TaxTypes"
import {
	LatestPGPaymentLink,
	usePaymentGatewayIntegratedAppsCount,
} from "../Integrations"

import { isDeepEqual } from "remeda"
import {
	AllowedPricingStrategyOptions,
	TPricingStrategyGroup,
	INewQuoteState,
} from "./NewQuote"
import { Markdown } from "@sembark-travel/ui-markdown"
import { TChildrenArray } from "../Tourists"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { useFlightAllocationsPricing, useFlightPricingConfig } from "../Flights"
import {
	ComponentMarkupTaxPricingBifurcation,
	getFlightComponentPriceCalculationValues,
} from "./TravelComponentPricingStrategy"

function XHR(xhr: XHRInstance) {
	return {
		async giveQuote(data: unknown): Promise<IGivenQuote> {
			return xhr.post(`/given-quotes`, data).then((resp) => resp.data.data)
		},
		async generatePGInvoice(
			givenQuoteId: number,
			payload: Record<string, unknown>
		): Promise<{ data: IGivenQuote }> {
			return xhr
				.post(`/given-quotes/${givenQuoteId}/pg-invoices`, payload)
				.then((resp) => resp.data)
		},
	}
}

export function GivenQuotePaymentLink({
	givenQuote,
	onChange,
	readOnly,
}: {
	givenQuote: Pick<
		IGivenQuote,
		"id" | "pg_invoice" | "given_currency" | "given_price"
	>
	onChange: () => void
	readOnly?: boolean
}) {
	const { pg_invoice, id } = givenQuote
	const xhr = useXHR()
	const [isDialogOpen, handleDialogOpen, handleDialogClose] = useDialog()
	return (
		<>
			{pg_invoice ? (
				<LatestPGPaymentLink
					paymentInvoice={pg_invoice}
					showAmount
					onRegenerate={() => handleDialogOpen()}
					regenerationLabel={
						<>
							<Icons.Pencil /> Modify
						</>
					}
					onChange={
						readOnly
							? undefined
							: () => {
									onChange()
								}
					}
				/>
			) : (
				<Button onClick={handleDialogOpen} size="sm" level="primary">
					<Icons.BankNotes /> Generate
				</Button>
			)}
			<Dialog
				open={isDialogOpen}
				onClose={handleDialogClose}
				sm
				title="Generate Payment Link"
			>
				<Form
					initialValues={{
						currency: givenQuote.given_currency,
						amount: formatMoneyByDecimal(
							givenQuote.pg_invoice
								? givenQuote.pg_invoice.original_amount
								: moneyParseByDecimal(
										givenQuote.given_price,
										givenQuote.given_currency
									)
						),
					}}
					onSubmit={withServerErrors(async (values) => {
						await XHR(xhr)
							.generatePGInvoice(id, {
								amount: values.amount,
								timezone_offset: config.timezoneOffset,
							})
							.then(onChange)
						handleDialogClose()
						showSnackbar("Payment Link generated successfully")
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<MoneyInputField
										currencyFieldName="currency"
										name="amount"
										label="Amount"
										secondaryLabel={givenQuote.given_currency}
									/>
									<Divider marginY="0" />
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Generating..." : "Generate Link"}
										</Button>
										<Button onClick={handleDialogClose} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Dialog.Body>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

/**
 * Per-person allocations saved before the flight-per-person update have no
 * `service` key. Those were always accommodation, so default to it.
 */
function withServiceFallback(
	allocations?: Array<Partial<TPricePerPersonAllocation>>
): Array<TPricePerPersonAllocation> | undefined {
	return allocations?.map(
		(allocation) =>
			({
				service: "accommodation",
				...allocation,
			}) as TPricePerPersonAllocation
	)
}
export function GiveQuote({
	onChange,
	tripQuote,
	onCancel,
	...props
}: {
	tripQuote: ITripQuote
	onChange?: () => void
	onCancel?: () => void
} & Omit<
	TGiveQuoteFormProps,
	| "initialValues"
	| "onSubmit"
	| "optionIndex"
	| "currencyPairs"
	| "costPriceCurrency"
	| "totalCostPrice"
	| "perPersonCostPriceAllocation"
	| "includePerPersonPrice"
	| "flightPrice"
	| "canModifyQuoteExchangeRate"
>) {
	const { hasPermission } = useCheckPermissions()
	const tripQuotes = useMemo(
		() => [tripQuote].concat(tripQuote.options || []),
		[tripQuote]
	)

	const minMarkupPercentage =
		tripQuote.trip?.tenant_brand?.min_margin_percentage
	const afterConfirmedQuote =
		tripQuote.used_for_holding ||
		tripQuote.used_for_conversion ||
		tripQuote.created_after_holding ||
		tripQuote.created_after_conversion
			? 1
			: 0
	const canModifyQuoteExchangeRate =
		Boolean(afterConfirmedQuote) ||
		hasPermission(PERMISSIONS.MODIFY_QUOTE_EXCHANGE_RATE)
	const tripId = tripQuote.trip_id
	const costPriceCurrency = tripQuote.currency

	const refreshTrip = useTripRefresh()
	const xhr = useXHR()
	const functionalCurrency = useFunctionalCurrencyOfTenant(costPriceCurrency)
	const tenantCurrencies = useCurrenciesOfTenant()
	const [initialValues] = useState<IGiveQuoteData>(() => {
		return getInitialValues({
			tripQuotes,
			afterConfirmedQuote,
			functionalCurrency,
			tenantCurrencies,
		})
	})
	return (
		<GiveQuoteForm
			initialValues={initialValues}
			canModifyQuoteExchangeRate={canModifyQuoteExchangeRate}
			minMarkupPercentage={minMarkupPercentage}
			{...props}
			onSubmit={async ({
				currency_pairs_obj,
				currency_pairs,
				options,
				pricing_strategy,
				...data
			}) => {
				const sharedData = {
					...data,
					currency_pairs: currencyPairsObjectToArray(currency_pairs_obj || {}),
					timezone_offset: config.timezoneOffset,
					tax_name: data.tax_name,
				}

				return XHR(xhr)
					.giveQuote({
						options: options.map(
							({
								cost_prices_per_person,
								gst_included,
								given_prices_per_component,
								...o
							}) => {
								return {
									...data,
									...o,
									...sharedData,
									gst_included: Number(gst_included),
									given_prices_per_component:
										prepareGivenPricesPerComponentPayloadDispatcher(
											pricing_strategy,
											given_prices_per_component
										),
								}
							}
						),
					})
					.then(() => {
						refreshTrip(tripId.toString())
						onChange?.()
					})
			}}
			onCancel={onCancel}
		/>
	)
}

interface IGiveQuoteData {
	tax_name: string
	// gst_included: "0" | "1" //Depricated - After the realease of new advance pricing stratgies
	// tax_percentage?: null | number //Depricated - After the realease of new advance pricing stratgies
	currency_pairs: Array<TCurrencyPair>
	currency_pairs_obj?: TCurrencyPairsObject
	given_currency: string
	complimentary_count: number
	no_of_adults: number
	children: TChildrenArray
	functional_currency: string
	costing_currency: string
	options: Array<{
		__id: number
		name: string
		trip_quote_id: number
		total_cost_price: number
		flight_price: number
		flight_cost_price: number
		margins?: IGivenQuote["margins"]
		per_component_cost_price_components?: {
			total: number
		}
		unrounded_given_prices?: {
			total: number
			subtotal: number
			per_component_allocation?: Record<TComponentKey, number>
			per_person_allocation?: Record<string, number>
		}
		cost_prices_per_person?: Array<TPricePerPersonAllocation>
		flight_given_prices_by_id?: Record<string, number>
		cost_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		cost_prices_per_component_distributed?: IQuoteComponentsCostPricesDistributed
		given_prices_per_person?: Array<TPricePerPersonAllocation>
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		// given_price_in_costing_currency: number //This has been depricated after release of new advance pricing strategies(Per Component & Per Component Per Person)
		given_price: number
		comments: string
		customer_remarks: string
		existing_given_price_currency?: string
		existing_given_price?: number
		existing_given_quote?: IGivenQuote
		gst_included: IGivenQuote["gst_included"]
		taxes: {
			editing_total: 0 | 1
			total_percentage: number
			total: number
			total_applied_on?: TTaxAppliedOn
			per_component_allocation: TTaxPerComponentAllocation
		}
	}>
	include_per_person_price?: 0 | 1 | "0" | "1" | boolean
	pricing_strategy: ITripQuote["pricing_strategy"]
	migrate_to_v2?: 0 | 1 //v1→v2 pricing strategy migration tracking
	after_confirmed_quote: 0 | 1
	enable_flight_rounding?: 0 | 1
	enable_sync_rounding_among_options?: 0 | 1
}

const defaultInitialValues: IGiveQuoteData = {
	tax_name: "",
	// gst_included: "1", //Depricated - After the realease of new advance pricing stratgies
	// tax_percentage: undefined, //Depricated - After the realease of new advance pricing stratgies
	currency_pairs: [],
	complimentary_count: 0,
	no_of_adults: 1,
	children: [],
	currency_pairs_obj: {},
	costing_currency: "INR",
	functional_currency: "INR",
	given_currency: "INR",
	options: [
		addKeyToFieldArrayItem({
			name: "",
			trip_quote_id: 0,
			total_cost_price: 0,
			flight_price: 0,
			flight_cost_price: 0,
			margins: {
				total: 0,
				total_percentage: 0,
				use_percentage: 0,
				editing_total: 1,
				rounding_point: 5,
			},
			given_prices_per_person: undefined,
			given_price: 0,
			comments: "",
			customer_remarks: "",
			gst_included: true,
			taxes: {
				editing_total: 1,
				total_percentage: 5,
				total: 0,
				total_applied_on: "cost_plus_markup",
				per_component_allocation: {
					hotels_applied_on: "cost_plus_markup",
					hotels_percentage: 0,
					hotels: 0,
					cabs_applied_on: "cost_plus_markup",
					cabs_percentage: 0,
					cabs: 0,
					activities_applied_on: "cost_plus_markup",
					activities_percentage: 0,
					activities: 0,
					extras_applied_on: "cost_plus_markup",
					extras_percentage: 0,
					extras: 0,
					flights_applied_on: "cost_plus_markup",
					flights_percentage: 0,
					flights: 0,
				},
			},
		}),
	],
	pricing_strategy: "overall_v2",
	after_confirmed_quote: 0,
}

/**
 * This component helps to prepare cost prices of all quote travel components
 * which returns all the prices in costing currency,
 * similarly to how hooks like "useTotalQuotePriceInCostingCurrency"
 * return prices for the components used on the quotation page main "NewQuoteForm".
 */

function getQuoteComponentsPricesInCostingCurrency(
	quoteComponents: ITripQuote["cost_prices_per_component"],
	costPriceCurrency: string,
	flightGivenPriceInCostingCurrency: number
): IQuoteComponentsCostPricesDistributed {
	const getPrice = (id: string) => {
		const comp = (quoteComponents || []).find((c) => c.id === id)
		return moneyParseByDecimal(Number(comp?.price || 0), costPriceCurrency)
	}

	const flightGivenPriceInCostingCurrencyMoney = moneyParseByDecimal(
		Number(flightGivenPriceInCostingCurrency || 0),
		costPriceCurrency
	)

	const totalHotelPrice = getPrice("hotels")
	const totalCabsOnlyPrice = getPrice("cabs")
	const totalTravelActivitiesOnlyPrice = getPrice("activities")
	const totalExtrasPrice = getPrice("extras")
	const flightCostPrice = getPrice("flights")

	const totalPrice = addMoney(
		totalHotelPrice,
		totalCabsOnlyPrice,
		totalTravelActivitiesOnlyPrice,
		totalExtrasPrice,
		flightGivenPriceInCostingCurrencyMoney
	)

	return {
		totalPrice, //total price in costing currency with flight given price in costing currency,
		flightPrice: flightGivenPriceInCostingCurrencyMoney, //flight given price in costing currency
		flightCostPrice, //flight cost price in costing currency
		totalHotelPrice,
		totalCabsOnlyPrice,
		totalTravelActivitiesOnlyPrice,
		totalExtrasPrice,
	}
}

function getInitialValues({
	tripQuotes,
	afterConfirmedQuote,
	functionalCurrency,
	tenantCurrencies,
}: {
	tripQuotes: Array<ITripQuote>
	afterConfirmedQuote: 0 | 1
	functionalCurrency?: TCurrency
	tenantCurrencies?: Array<TCurrency>
}): IGiveQuoteData {
	const firstTripQuote = tripQuotes[0]
	const existingGivenQuote = firstTripQuote.given_quote
	const gstIncluded = existingGivenQuote
		? existingGivenQuote.gst_included
			? "1"
			: "0"
		: "1"

	const tax_percentage =
		existingGivenQuote?.tax_percentage === null ||
		existingGivenQuote?.tax_percentage === undefined
			? 5
			: existingGivenQuote?.tax_percentage
	const quote = firstTripQuote.quote
	const costPriceCurrency = firstTripQuote.currency
	const givenPriceCurrrency =
		existingGivenQuote?.given_currency || costPriceCurrency
	const currencyPairs =
		existingGivenQuote?.currency_pairs || quote.currency_pairs || []

	const pricing_strategy = firstTripQuote?.pricing_strategy || "overall_v2"
	const noOfAdults = quote.no_of_adults
	const children = quote.children
	const complimentaryCount = quote.complimentary_count

	// Find the currency object for the given price currency to get its default rounding
	const givenCurrencyObject = tenantCurrencies?.find(
		(c) => c.currency === givenPriceCurrrency
	)
	const defaultRoundingPoint = givenCurrencyObject?.rounding
		? Number(givenCurrencyObject.rounding)
		: 5

	const flightGivenPricesById =
		firstTripQuote.given_quote?.given_prices_per_person
			?.filter((allocation) => allocation.service === "flight")
			.reduce<Record<string, number>>((acc, allocation) => {
				acc[allocation.id] = Number(allocation?.price_in_costing_currency)
				return acc
			}, {}) ?? {}
	return {
		tax_name: existingGivenQuote?.tax_name || "",
		costing_currency: costPriceCurrency,
		given_currency: givenPriceCurrrency,
		complimentary_count: complimentaryCount,
		no_of_adults: noOfAdults,
		children: children,
		currency_pairs: currencyPairs,
		currency_pairs_obj: currencyPairsArrayToObject(currencyPairs),
		functional_currency: functionalCurrency?.currency || costPriceCurrency,
		options: tripQuotes.map(function (tripQuote, index) {
			const flightPrice = !tripQuote //flight given price in costing currency (user inputed given price for flight inside given price input field for flight prices)
				? 0
				: Number(
						formatMoneyByDecimal(
							makeCurrencyConverter(currencyPairs)(
								firstTripQuote.currency,
								getTotalPriceForExtras(
									firstTripQuote.quote?.flights.map((f) => ({
										...f,
										price: f.given_price,
									}))
								)
							)
						)
					)

			const flightCostPrice = !tripQuote //flight cost price in costing currency (user inputed cost price for flight inside cost price input field for flight prices)
				? 0
				: Number(
						formatMoneyByDecimal(
							makeCurrencyConverter(currencyPairs)(
								firstTripQuote.currency,
								getTotalPriceForExtras(
									firstTripQuote.quote?.flights.map((f) => ({
										...f,
										price: f.calculated_price,
									}))
								)
							)
						)
					)
			const existingGivenQuote = tripQuote.given_quote

			// Legacy allocations have no `service`;
			// adding service : accommodation as fallback in cost and given per person prices
			const costPricesPerPerson = withServiceFallback(
				tripQuote.cost_prices_per_person
			)
			const givenPricesPerPerson = withServiceFallback(
				existingGivenQuote?.given_prices_per_person
			)

			const totalCostPrice = tripQuote.cost_price

			const margin = existingGivenQuote
				? calculateMarginFromTotalPrice({
						givenPrice: existingGivenQuote.given_price,
						costPriceWithoutFlights: totalCostPrice - (flightCostPrice || 0),
						flightPrice: flightCostPrice,
						gstIncluded: Boolean(Number(gstIncluded)),
						taxPercentage: tax_percentage,
					})
				: 0

			const margin_percentage = calculateMarginPercentageFromTotalPrice({
				costPriceWithoutFlights: totalCostPrice - (flightCostPrice || 0),
				margin,
			})

			const cost_prices_per_component_distributed =
				getQuoteComponentsPricesInCostingCurrency(
					tripQuote.cost_prices_per_component,
					costPriceCurrency,
					flightPrice
				)

			const gstIncludedForOption = existingGivenQuote
				? existingGivenQuote.gst_included
					? "1"
					: "0"
				: "1"

			const givenQuoteTaxes = tripQuote?.given_quote?.taxes
			const givenQuoteTaxesPerComponentAllocation =
				givenQuoteTaxes?.per_component_allocation

			const taxes = {
				editing_total: givenQuoteTaxes ? givenQuoteTaxes.editing_total : 1,
				total_percentage: givenQuoteTaxes
					? givenQuoteTaxes.total_percentage
					: (existingGivenQuote?.tax_type?.default_percentage_value ?? 0),
				total: givenQuoteTaxes ? givenQuoteTaxes.total : 0,
				total_applied_on: givenQuoteTaxes
					? givenQuoteTaxes?.total_applied_on || undefined
					: "cost_plus_markup",
				per_component_allocation: {
					hotels_applied_on:
						givenQuoteTaxesPerComponentAllocation?.hotels_applied_on ||
						"cost_plus_markup",
					hotels_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.hotels_percentage
						: 0,
					hotels: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.hotels
						: 0,
					cabs_applied_on:
						givenQuoteTaxesPerComponentAllocation?.cabs_applied_on ||
						"cost_plus_markup",
					cabs_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.cabs_percentage
						: 0,
					cabs: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.cabs
						: 0,
					activities_applied_on:
						givenQuoteTaxesPerComponentAllocation?.activities_applied_on ||
						"cost_plus_markup",
					activities_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.activities_percentage
						: 0,
					activities: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.activities
						: 0,
					extras_applied_on:
						givenQuoteTaxesPerComponentAllocation?.extras_applied_on ||
						"cost_plus_markup",
					extras_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.extras_percentage
						: 0,
					extras: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.extras
						: 0,
					flights_applied_on:
						givenQuoteTaxesPerComponentAllocation?.flights_applied_on ||
						"cost_plus_markup",
					flights_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.flights_percentage
						: 0,
					flights: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.flights
						: 0,
				},
			}

			return addKeyToFieldArrayItem({
				name: tripQuote.name || `Option ${index + 1}`,
				trip_quote_id: tripQuote.id,
				total_cost_price: totalCostPrice,
				flight_price: flightPrice,
				flight_cost_price: flightCostPrice,
				given_prices_per_person: givenPricesPerPerson,
				margins: {
					total: margin,
					total_percentage: margin_percentage,
					use_percentage: 1 as const,
					editing_total: existingGivenQuote?.margins ? 0 : 1,
					// we are not overrinding the existing margins if they are present,
					...existingGivenQuote?.margins,
					// Only set default rounding for new quotes (when no existing margins)
					// When editing (margins exist), keep existing rounding_point even if undefined
					rounding_point: existingGivenQuote?.margins
						? existingGivenQuote?.margins?.rounding_point
						: defaultRoundingPoint,
				},
				cost_prices_per_person: costPricesPerPerson,
				flight_given_prices_by_id: flightGivenPricesById,
				cost_prices_per_component: tripQuote.cost_prices_per_component,
				cost_prices_per_component_distributed:
					cost_prices_per_component_distributed,
				given_price: existingGivenQuote?.given_price || 0,
				existing_given_price_currency: existingGivenQuote?.given_currency,
				existing_given_price: existingGivenQuote?.given_price,
				existing_given_quote: existingGivenQuote,
				comments: "",
				customer_remarks: existingGivenQuote?.customer_remarks || "",
				gst_included: isTruthy(gstIncludedForOption),
				taxes: taxes,
			})
		}),
		include_per_person_price: Boolean(
			firstTripQuote?.cost_prices_per_person?.length
		),
		pricing_strategy: pricing_strategy,
		after_confirmed_quote: afterConfirmedQuote,
	}
}

const giveQuoteSchema = Validator.object()
	.shape({
		// given_price: Validator.number()
		//   .positive("Selling price should a positive number")
		//   .required("Selling price field is required"),
		// comments: Validator.string(),
	})
	.required("Quote data is required")

type TGiveQuoteFormProps = {
	initialValues?: IGiveQuoteData
	onSubmit: (
		data: Omit<IGiveQuoteData, "options" | "include_per_person_price"> & {
			options: Array<
				Omit<IGiveQuoteData["options"][number], "taxes"> & {
					taxes?: IGiveQuoteData["options"][number]["taxes"]
				}
			>
		}
	) => Promise<void>
	onCancel?: () => void
	readOnly?: boolean
	onRefetchInitialValues?: () => void
	canModifyQuoteExchangeRate: boolean
	minMarkupPercentage?: number
} & Omit<
	React.ComponentProps<typeof MarginsAndTaxInputField>,
	| "costPriceWithoutFlights"
	| "flightPriceInCostingCurrency"
	| "optionIndex"
	| "perPersonCostPriceAllocation"
	| "includePerPersonPrice"
	| "quoteComponentPrices"
	| "enableFlightRounding"
	| "enableSyncRoundingAmongOptions"
>

const validate = validateFormValues(giveQuoteSchema)

function GiveQuoteForm({
	initialValues = defaultInitialValues,
	onSubmit,
	onCancel,
	onRefetchInitialValues,
	canModifyQuoteExchangeRate,
	minMarkupPercentage,
	...props
}: TGiveQuoteFormProps) {
	const collapseStrategyToGroup = useCallback(
		(s: TGivenQuotePricingStrategy): TPricingStrategyGroup => {
			if (s === "overall_v1" || s === "overall_v2") return "overall"
			if (s === "per_person_v1" || s === "per_person_v2") return "per_person"
			if (s === "per_component_v2") return "per_component"
			return "per_component_per_person"
		},
		[]
	)
	return (
		<Form<IGiveQuoteData>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(
				async ({
					options,
					pricing_strategy,
					migrate_to_v2,
					after_confirmed_quote,
					enable_sync_rounding_among_options,
					include_per_person_price,
					...values
				}) => {
					if (options.filter((o) => !o.given_price).length) {
						throw new Error("Package price is invalid.")
					}
					// currently we are not supporting per option taxes,
					// but in future we may support that's currently on the frontend for per-component we have synced taxes based on option 1
					// as because backend api needs taxes for each option

					const option1Taxes = options[0].taxes

					const isGstIncluded = options[0].gst_included

					const option1TaxesPayload = isGstIncluded
						? {
								...option1Taxes,
							}
						: undefined

					const option1Margins = options[0].margins
					const option1CostPricesPerComponent =
						options[0].cost_prices_per_component

					// Determine if we should upgrade v1 strategies to v2 based on flight feature usage (mainly rounding applied or not on flights)
					const finalPricingStrategy = determineFinalPricingStrategy(
						pricing_strategy,
						true, //Give Quote Form is mainly designed for editing existing quote prices that's why (ie. we assume that editingExisitngQuote will be true always here)
						// isTruthy(after_confirmed_quote),
						migrate_to_v2
					)
					await onSubmit({
						pricing_strategy: finalPricingStrategy,
						after_confirmed_quote,
						options: options.map(
							({
								margins,
								taxes,
								per_component_cost_price_components,
								cost_prices_per_component,
								cost_prices_per_person,
								unrounded_given_prices,
								given_price,
								given_prices_per_person,
								flight_given_prices_by_id,
								total_cost_price,
								...o
							}) => {
								//@dev-note - we have to sync margins for all options expect margin for hotels as per current requirement but in future,
								//it can be possible if give user option to add indiviudal markups for options

								//dev-note- Currently, on the quotation page, markups are not synced for old(overall, per_person) stratgies,
								//so we have handle this
								//by sending their independent margins of per option for old strategies. The new stratgies (per_component, per_component_per_person) have the feature of sync markups expect hotels on the qutation form.
								const optionMargins = prepareMarginsPayloadForOptionDispatcher(
									margins,
									option1Margins,
									pricing_strategy,
									per_component_cost_price_components?.total || 0,
									enable_sync_rounding_among_options,
									cost_prices_per_component,
									cost_prices_per_person,
									option1CostPricesPerComponent
								)
								const totalMarkupPrice =
									(optionMargins?.total || 0) +
									(optionMargins?.per_component_allocation?.flights || 0)

								const markupPercentage =
									(totalMarkupPrice / Number(total_cost_price)) * 100

								if (
									minMarkupPercentage !== null &&
									minMarkupPercentage !== undefined &&
									minMarkupPercentage > markupPercentage
								) {
									throw Error(
										`Markup must be atleast ${minMarkupPercentage}% of total cost price in ${o.name} Option`
									)
								}

								const isGstIncludedForOption = isGstIncluded

								const taxPerComponentAllocation =
									preparePerComponentTaxAllocationPayloadDispatcher(
										pricing_strategy,
										option1Taxes,
										taxes,
										cost_prices_per_component
									)

								const globalTaxAppliedOn =
									prepareGlobalTaxAppliedOnPayloadDispatcher(
										pricing_strategy,
										option1Taxes,
										taxPerComponentAllocation
									)

								const unroundedGivenPrices = unrounded_given_prices
									? {
											...unrounded_given_prices,
											per_person_allocation:
												include_per_person_price && given_price
													? unrounded_given_prices.per_person_allocation
													: undefined,
											per_component_allocation:
												prepareUnroundedGivenPricesPerComponentPayloadDispatcher(
													pricing_strategy,
													unrounded_given_prices
												),
										}
									: undefined

								return {
									...o,
									total_cost_price,
									cost_prices_per_person,
									per_component_cost_price_components,
									cost_prices_per_component,
									given_price,
									given_prices_per_person:
										include_per_person_price && given_price
											? given_prices_per_person
											: undefined,
									unrounded_given_prices: unroundedGivenPrices,
									taxes: option1TaxesPayload
										? {
												...option1TaxesPayload,
												total: taxes.total,
												total_percentage:
													prepareTaxPercentagePayloadForOptionDispatcher(
														finalPricingStrategy,
														option1Taxes,
														taxes.total,
														per_component_cost_price_components?.total || 0,
														optionMargins?.total || 0,
														globalTaxAppliedOn
													),
												per_component_allocation: taxPerComponentAllocation,
												total_applied_on: globalTaxAppliedOn,
												editing_total:
													prepareTaxEditingTotalPayloadForOptionDispatcher(
														finalPricingStrategy,
														option1Taxes
													),
											}
										: undefined,
									gst_included: isGstIncludedForOption,
									margins: optionMargins,
								}
							}
						),
						...values,
					})
					showSnackbar("Package price updated.")
				}
			)}
			subscription={{
				submitting: true,
			}}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, form, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{!props.readOnly ? (
							<SelectGivenCurrency
								canModifyQuoteExchangeRate={canModifyQuoteExchangeRate}
							/>
						) : null}
						{props.readOnly ? (
							<GetFieldValue<
								IGiveQuoteData["pricing_strategy"]
							> name="pricing_strategy">
								{({ value: pricingStrategy }) => (
									<Inline gap="4">
										<Text fontWeight="semibold">Pricing Strategy</Text>
										<SelectInput
											label="Pricing Strategy"
											disabled
											size="sm"
											style={{ width: "220px" }}
											value={collapseStrategyToGroup(pricingStrategy)}
										>
											<AllowedPricingStrategyOptions />
										</SelectInput>
									</Inline>
								)}
							</GetFieldValue>
						) : null}

						<FieldArray<IGiveQuoteData["options"][number]> name="options">
							{({ fields }) => (
								<Stack gap="6">
									{fields.map((name, index) => {
										const key = getFieldArrayItemKey(form as never, name)
										return (
											<Box key={key}>
												<Stack gap="4" position="relative">
													{Number(fields.length || 0) > 1 ? (
														<Box position="sticky" top="0" zIndex="10">
															<Box
																position="sticky"
																top="0"
																zIndex="10"
																bgColor="emphasis"
																color="on_emphasis"
																padding="2"
																rounded="md"
															>
																<Heading fontSize="md">
																	Option {index + 1}:{" "}
																	<GetFieldValue<string> name={`${name}.name`}>
																		{({ value }) => <>{value}</>}
																	</GetFieldValue>
																</Heading>
															</Box>
														</Box>
													) : null}
													<Stack gap="4" flex="1" maxWidth="full">
														<GetFieldValue<
															IGiveQuoteData["pricing_strategy"]
														> name="pricing_strategy">
															{({ value: pricingStrategy }) => (
																<GetFieldValue<
																	IGiveQuoteData["options"][number]["total_cost_price"]
																>
																	name={`${name}.total_cost_price`}
																>
																	{({ value: totalCostPrice }) => (
																		<GetFieldValue<
																			IGiveQuoteData["options"][number]["flight_price"]
																		>
																			name={`${name}.flight_price`}
																		>
																			{({ value: flightPrice }) => (
																				<GetFieldValue<
																					IGiveQuoteData["options"][number]["cost_prices_per_person"]
																				>
																					name={`${name}.cost_prices_per_person`}
																				>
																					{({
																						value: perPersonCostPriceAllocation,
																					}) => (
																						<GetFieldValue<
																							IGiveQuoteData["options"][number]["cost_prices_per_component_distributed"]
																						>
																							name={`${name}.cost_prices_per_component_distributed`}
																						>
																							{({
																								value:
																									costPricesPerComponentDistributed,
																							}) => {
																								if (
																									pricingStrategy ===
																										"per_component_v2" &&
																									costPricesPerComponentDistributed
																								) {
																									return (
																										<ComponentMarkupTaxPricingBifurcation
																											optionIndex={index}
																											quoteComponentPrices={
																												costPricesPerComponentDistributed
																											}
																											{...props}
																										/>
																									)
																								}

																								if (
																									pricingStrategy ===
																										"per_component_per_person_v2" &&
																									costPricesPerComponentDistributed
																								) {
																									return (
																										<ComponentMarkupTaxPricingBifurcation
																											optionIndex={index}
																											quoteComponentPrices={
																												costPricesPerComponentDistributed
																											}
																											isPerPersonPricingEnabled={
																												true
																											}
																											{...props}
																										/>
																									)
																								}

																								if (
																									costPricesPerComponentDistributed
																								) {
																									return (
																										<GetFieldValue<
																											IGiveQuoteData["options"][number]["flight_cost_price"]
																										>
																											name={`${name}.flight_cost_price`}
																										>
																											{({
																												value:
																													flightCostPriceInCostingCurrency,
																											}) => {
																												return (
																													<GetFieldValue<
																														IGiveQuoteData["after_confirmed_quote"]
																													> name="after_confirmed_quote">
																														{({
																															value:
																																afterConfirmedQuote,
																														}) => (
																															<GetFieldValue<
																																IGiveQuoteData["include_per_person_price"]
																															> name="include_per_person_price">
																																{({
																																	value:
																																		includePerPersonPrice,
																																}) => (
																																	<PerPersonCostPrice
																																		optionIndex={
																																			index
																																		}
																																		costPriceWithoutFlights={
																																			Number(
																																				totalCostPrice
																																			) -
																																			Number(
																																				flightCostPriceInCostingCurrency ||
																																					0
																																			)
																																		}
																																		quoteComponentPrices={
																																			costPricesPerComponentDistributed
																																		}
																																		flightPriceInCostingCurrency={
																																			flightPrice
																																		}
																																		flightCostPriceInCostingCurrency={
																																			flightCostPriceInCostingCurrency
																																		}
																																		perPersonCostPriceAllocation={
																																			perPersonCostPriceAllocation
																																		}
																																		includePerPersonPrice={isTruthy(
																																			includePerPersonPrice
																																		)}
																																		pricingStrategy={
																																			pricingStrategy
																																		}
																																		afterConfirmedQuote={isTruthy(
																																			afterConfirmedQuote
																																		)}
																																		{...props}
																																	/>
																																)}
																															</GetFieldValue>
																														)}
																													</GetFieldValue>
																												)
																											}}
																										</GetFieldValue>
																									)
																								}

																								return null
																							}}
																						</GetFieldValue>
																					)}
																				</GetFieldValue>
																			)}
																		</GetFieldValue>
																	)}
																</GetFieldValue>
															)}
														</GetFieldValue>

														<Grid gap="4">
															<Col sm={12} md>
																<TextAreaInputField
																	name={`${name}.comments`}
																	label="Any internal Comments"
																	placeholder="Write internal comments regarding prices or anything else..."
																	secondaryLabel="optional"
																	readOnly={props.readOnly}
																/>
															</Col>

															<Col sm={12} md>
																<Stack gap="2">
																	<MarkdownInputField
																		label="Remarks for Agent/Customer"
																		placeholder="Any special remarks for the customer."
																		secondaryLabel="optional"
																		name={`${name}.customer_remarks`}
																		readOnly={props.readOnly}
																	/>
																	<Text
																		fontSize="sm"
																		color="muted"
																		style={{ fontStyle: "italic" }}
																	>
																		These remarks will be shared with the
																		customer.
																	</Text>
																</Stack>
															</Col>
														</Grid>

														{props.readOnly && onRefetchInitialValues ? (
															<GetFieldValue<
																IGiveQuoteData["options"][number]["existing_given_quote"]
															>
																name={`${name}.existing_given_quote`}
															>
																{({ value: existingGivenQuote }) => {
																	return existingGivenQuote ? (
																		<DisplayGivenQuotePricing
																			givenQuote={existingGivenQuote}
																			onChange={onRefetchInitialValues}
																		/>
																	) : null
																}}
															</GetFieldValue>
														) : null}
													</Stack>
												</Stack>
											</Box>
										)
									})}
								</Stack>
							)}
						</FieldArray>
						{!props.readOnly ? (
							<>
								<Stack
									borderWidth="2"
									borderColor="primary"
									rounded="md"
									padding="4"
									bgColor="accent"
									gap="4"
								>
									<Inline gap="2" as="header">
										<Box>
											<Box
												display="inlineFlex"
												size="12"
												alignItems="center"
												justifyContent="center"
												bgColor="primary_emphasis"
												color="on_emphasis"
												rounded="full"
											>
												<Icons.BankNotes size="6" />
											</Box>
										</Box>
										<Stack>
											<Heading as="h4" id="quote_selling_price">
												Preview Final Price
											</Heading>
											<Text color="muted">
												Here are the final prices for this option.
											</Text>
										</Stack>
									</Inline>
									<FieldArray<IGiveQuoteData["options"][number]> name="options">
										{({ fields }) => (
											<Stack gap="6">
												{fields.map((name, index) => {
													const key = getFieldArrayItemKey(form as never, name)
													return (
														<Grid gap="4" key={key} position="relative">
															{Number(fields.length || 0) > 1 ? (
																<Col
																	style={{ position: "sticky" }}
																	xs={12}
																	lg={3}
																	top="0"
																	zIndex="10"
																>
																	<Box
																		position="sticky"
																		top="0"
																		zIndex="10"
																		bgColor="emphasis"
																		color="on_emphasis"
																		padding="2"
																		rounded="md"
																	>
																		<Text fontSize="sm">
																			Option {index + 1}
																		</Text>
																		<Heading fontSize="md">
																			<GetFieldValue<string>
																				name={`${name}.name`}
																			>
																				{({ value }) => <>{value}</>}
																			</GetFieldValue>
																		</Heading>
																	</Box>
																</Col>
															) : null}
															<Col>
																<Stack gap="2">
																	<GetFieldValue<
																		IGiveQuoteData["options"][number]["flight_cost_price"]
																	>
																		name={`${name}.flight_cost_price`}
																	>
																		{({ value: flightCostPrice }) => (
																			<GetFieldValue<
																				IGiveQuoteData["options"][number]["flight_price"]
																			>
																				name={`${name}.flight_price`}
																			>
																				{({ value: flightGivenPrice }) => (
																					<PackagePricePreview
																						flightGivenPriceInCostingCurrency={
																							flightGivenPrice
																						}
																						flightCostPriceInCostingCurrency={
																							flightCostPrice
																						}
																						optionIndex={index}
																					/>
																				)}
																			</GetFieldValue>
																		)}
																	</GetFieldValue>

																	<GetFieldValue<string>
																		name={`${name}.customer_remarks`}
																	>
																		{({ value: customer_remarks }) =>
																			customer_remarks ? (
																				<Stack
																					fontSize="sm"
																					color="success"
																					maxWidth="3xl"
																					gap="2"
																				>
																					Customer Remarks:{" "}
																					<Markdown>
																						{customer_remarks}
																					</Markdown>
																				</Stack>
																			) : null
																		}
																	</GetFieldValue>
																</Stack>
															</Col>
														</Grid>
													)
												})}
											</Stack>
										)}
									</FieldArray>
								</Stack>
								<SubmissionError />
								<Divider sm />
								<Inline gap="4">
									<Button
										type="button"
										onClick={() => form.submit()}
										disabled={submitting}
										level="primary"
									>
										{submitting ? "Updating price..." : "Set Selling Price"}
									</Button>
									{onCancel ? (
										<Button onClick={onCancel} level="tertiary">
											Cancel
										</Button>
									) : null}
								</Inline>
							</>
						) : null}
					</Stack>
				</form>
			)}
		</Form>
	)
}

function PerPersonCostPrice({
	optionIndex,
	afterConfirmedQuote,
	pricingStrategy,
	quoteComponentPrices,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	perPersonCostPriceAllocation,
	includePerPersonPrice,
	...props
}: {
	optionIndex: number
	afterConfirmedQuote: boolean
	pricingStrategy: IGiveQuoteData["pricing_strategy"]
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency: number
	perPersonCostPriceAllocation: ITripQuote["cost_prices_per_person"]
	includePerPersonPrice: boolean
	readOnly?: boolean
}) {
	const { enableFlightRounding, enableSyncRoundingAmongOptions } =
		usePricingStrategyMigrationSync({
			pricingStrategy,
			editingExistingQuote: true, //Give Quote Form is mainly designed for editing existing quote prices that's why (ie. we assume that editingExisitngQuote will be true always here)
		})

	return (
		<MarginsAndTaxInputField
			optionIndex={optionIndex}
			costPriceWithoutFlights={costPriceWithoutFlights}
			quoteComponentPrices={quoteComponentPrices}
			flightPriceInCostingCurrency={flightPriceInCostingCurrency}
			flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency}
			perPersonCostPriceAllocation={perPersonCostPriceAllocation}
			includePerPersonPrice={includePerPersonPrice}
			enableFlightRounding={enableFlightRounding}
			enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
			{...props}
		/>
	)
}

export function SelectGivenCurrency({
	canModifyQuoteExchangeRate,
}: {
	canModifyQuoteExchangeRate: boolean
}) {
	const form = useForm()
	const allCurrencies = useCurrenciesOfTenant()
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { value: options } = useFieldValue<IGiveQuoteData["options"]>("options")
	const { value: enableSyncRoundingAmongOptions } = useFieldValue<
		0 | 1 | undefined
	>("enable_sync_rounding_among_options")

	const handleCurrencyChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const newCurrency = e.target.value
			form.change("given_currency", newCurrency)

			// Find the currency object for the selected currency
			const selectedCurrency = allCurrencies?.find(
				(c) => c.currency === newCurrency
			)

			// Get the default rounding point from the currency
			const defaultRoundingPoint = selectedCurrency?.rounding
				? Number(selectedCurrency.rounding)
				: 5

			// Update the rounding point for all options (or just option 0 if sync is enabled)
			if (enableSyncRoundingAmongOptions) {
				// Only update option 0 when sync is enabled
				form.change(`options[0].margins.rounding_point`, defaultRoundingPoint)
			} else {
				// Update all options when sync is disabled
				options?.forEach((_, index) => {
					form.change(
						`options[${index}].margins.rounding_point`,
						defaultRoundingPoint
					)
				})
			}
		},
		[allCurrencies, options, enableSyncRoundingAmongOptions, form]
	)

	if (!allCurrencies.length) return null
	return (
		<Inline gap="4">
			<SelectInputField
				label="Selling Currency"
				name="given_currency"
				onChange={handleCurrencyChange}
			>
				<option value={givenPriceCurrency} key={givenPriceCurrency}>
					{givenPriceCurrency}
				</option>
				{allCurrencies.map((c) =>
					c.currency === givenPriceCurrency ? null : (
						<option value={c.currency} key={c.currency}>
							{c.currency}
						</option>
					)
				)}
			</SelectInputField>
			{costPriceCurrency !== givenPriceCurrency ? (
				<Box>
					<CurrencyPairInputFieldWithRates
						canNotCustomize={!canModifyQuoteExchangeRate}
						key={`${costPriceCurrency}-${givenPriceCurrency}`}
						baseCurrency={costPriceCurrency}
						counterCurrency={givenPriceCurrency}
						name={`currency_pairs_obj`}
						readOnly={costPriceCurrency === givenPriceCurrency}
						style={{ maxWidth: "120px" }}
					/>
				</Box>
			) : null}
		</Inline>
	)
}

export function MarginsAndTaxInputField({
	optionIndex,
	quoteComponentPrices,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	perPersonCostPriceAllocation,
	includePerPersonPrice = false,
	enableFlightRounding = false,
	enableSyncRoundingAmongOptions = false,
	...props
}: {
	optionIndex: number
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency?: number
	perPersonCostPriceAllocation: ITripQuote["cost_prices_per_person"]
	includePerPersonPrice: boolean
	enableFlightRounding: boolean
	enableSyncRoundingAmongOptions: boolean
	readOnly?: boolean
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { onChange: changeOptionMarkupUsePercentage } = useFieldValue(
		`options[${optionIndex}].margins.use_percentage`
	)

	// Get global tax applied on value for display in rows
	const { value: globalTaxAppliedOn } = useFieldValue<string>(
		"options[0].taxes.total_applied_on"
	)
	const { value: gstIncluded } = useFieldValue<boolean>(
		"options[0].gst_included"
	)

	const globalTaxEnabled = isTruthy(gstIncluded)

	const hasPerPersonPrices =
		perPersonCostPriceAllocation?.length && includePerPersonPrice ? true : false

	const { readOnly: readOnlyMode = false } = props
	const roundOptions = useMemo(() => {
		return hasPerPersonPrices
			? [undefined, 1, 5, 10, 50, 100]
			: [undefined, 1, 5, 10, 50, 100, 500]
	}, [hasPerPersonPrices])

	const optionRoundingPointIndex = enableSyncRoundingAmongOptions
		? 0
		: optionIndex //if enableSyncRoundingAmongOptions is true, then the rounding point is the same for all options
	const {
		flight: flightAllocations = [],
		accommodation: accommodationAllocations = [],
	} = useMemo(
		() => partitionPerPersonAllocations(perPersonCostPriceAllocation || []),
		[perPersonCostPriceAllocation]
	)

	const showFlightsSection =
		Number(flightCostPriceInCostingCurrency || 0) > 0 ||
		(includePerPersonPrice && flightAllocations.length > 0)

	const subTotalRowLabel = showFlightsSection ? "Sub-Total" : "Total"
	return (
		<Stack gap="4">
			<Table bordered hover responsive>
				<thead>
					<tr>
						<TableHeaderDataCell></TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset" textAlign="right">
							<Inline gap="1" justifyContent="end" flexWrap="wrap">
								<Text>Cost Price</Text>
								<Text>({costPriceCurrency})</Text>
							</Inline>
						</TableHeaderDataCell>
						<TableHeaderDataCell
							bgColor="inset"
							style={{
								width: "100px",
							}}
						>
							<Stack gap="1" textAlign={"center"}>
								<Inline gap="2" justifyContent="center">
									<Text>Markup</Text>
									<Tooltip
										interactive
										content="Set the minimum markup required to save a quote"
									>
										<Box>
											<Icons.Info />
										</Box>
									</Tooltip>
								</Inline>
								<SelectInputField
									disabled={readOnlyMode}
									name={`options[${optionIndex}].margins.use_percentage`}
									size="sm"
									onChange={(e) => {
										const value = e.currentTarget.value

										if (value === "0") {
											changeOptionMarkupUsePercentage(0)
										} else {
											changeOptionMarkupUsePercentage(1)
										}
									}}
								>
									<option value={0} label="Amount" title="Amount">
										Amount
									</option>
									<option value={1} label="Percentage" title="Percentage">
										Percentage
									</option>
								</SelectInputField>
							</Stack>
						</TableHeaderDataCell>
						<TableHeaderDataCell
							bgColor="inset"
							style={{
								width: "100px",
							}}
						>
							<Stack gap="1" textAlign="center">
								<Text>Tax Applied On</Text>
								{isTruthy(gstIncluded) ? (
									<SelectInputField
										disabled={readOnlyMode}
										name={`options[0].taxes.total_applied_on`}
										size="sm"
									>
										<option value="cost_plus_markup">Cost + Markup</option>
										<option value="only_markup">Only Markup</option>
									</SelectInputField>
								) : (
									<Text fontSize="sm" color="muted">
										-
									</Text>
								)}
							</Stack>
						</TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset">
							<Component initialState={false}>
								{({ state: taxDisabled, setState: setTaxDisabled }) => (
									<GetFieldValue<boolean | 0 | 1>
										name={`options[0].gst_included`}
									>
										{({
											value: gstIncluded,
											onChange: onTaxInclusionChange,
										}) => (
											<Inline
												gap="1"
												alignItems="center"
												justifyContent="center"
												flexWrap="wrap"
											>
												<Inline
													gap="1"
													alignItems="center"
													justifyContent="center"
												>
													{taxDisabled ? null : (
														<SwitchInputField
															disabled={readOnlyMode}
															name="options[0].gst_included"
														/>
													)}
													<GetFieldValue<
														number | ""
													> name="options[0].taxes.total_percentage">
														{({ onChange: changeTaxPercentage }) => (
															<InlineSelectTaxTypeInputField
																name="tax_name"
																onChangeDefaultValue={(value) => {
																	changeTaxPercentage(value)
																}}
																disabled={readOnlyMode}
																onDisabledTaxValueSelection={(disabled) => {
																	setTaxDisabled(disabled)
																	if (disabled) {
																		onTaxInclusionChange(false)
																	}
																}}
															/>
														)}
													</GetFieldValue>
												</Inline>
												<Box>
													{isTruthy(gstIncluded) ? (
														<PercentageInputField
															name="options[0].taxes.total_percentage"
															size="sm"
															style={{ width: "60px" }}
															disabled={readOnlyMode}
														/>
													) : taxDisabled ? null : (
														<Text>(exc)</Text>
													)}
												</Box>
											</Inline>
										)}
									</GetFieldValue>
								)}
							</Component>
						</TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset" textAlign="right">
							<Inline gap="1" justifyContent="end" flexWrap="wrap">
								<Text>Total</Text>
								<Text>({costPriceCurrency})</Text>
							</Inline>
						</TableHeaderDataCell>
						{givenPriceCurrency && givenPriceCurrency !== costPriceCurrency ? (
							<TableHeaderDataCell textAlign="right" bgColor={"inset"}>
								<Inline gap="1" justifyContent="end" flexWrap="wrap">
									<Text>Total</Text>
									<Text>({givenPriceCurrency})</Text>
								</Inline>
							</TableHeaderDataCell>
						) : null}
						<TableHeaderDataCell
							textAlign="right"
							borderLeftWidth="1"
							bgColor="primary"
						>
							<Inline
								gap="1"
								justifyContent="end"
								alignItems="center"
								flexWrap="wrap"
							>
								<Text>Round</Text>
								<Box
									paddingX="2"
									bgColor="default"
									rounded="default"
									borderWidth="1"
								>
									<InlineSelectInputField
										name={`options[${optionRoundingPointIndex}].margins.rounding_point`}
										style={{ maxWidth: "90px" }}
										disabled={readOnlyMode}
									>
										{roundOptions.map((o, i) => (
											<option value={o || ""} key={i}>
												{o || "--"}
											</option>
										))}
									</InlineSelectInputField>
								</Box>
							</Inline>
						</TableHeaderDataCell>
					</tr>
				</thead>
				{hasPerPersonPrices ? (
					<tbody>
						{accommodationAllocations.map(
							({ id, per_pax_label, total_pax_label, pax_count, price }) =>
								!Number(price) ? null : (
									<tr
										key={`options-${optionIndex}-${id}-${total_pax_label}-${price}`}
									>
										<TableDataCell bgColor="inset" fontWeight="semibold">
											<Stack>
												<Text>/{per_pax_label}</Text>
												<Text fontSize="sm" color="muted">
													{total_pax_label}
												</Text>
											</Stack>
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Money
												currency={costPriceCurrency}
												amount={Number(price)}
											/>
											<Text color="muted" fontSize="sm">
												x {pax_count}
											</Text>
										</TableDataCell>
										<TableDataCell textAlign="center">
											<Stack alignItems="center">
												<PerPersonMarginInputField
													disabled={readOnlyMode}
													optionIndex={optionIndex}
													name={`options[${optionIndex}].margins.per_person_allocation.${id}`}
													costPrice={Number(price)}
												/>
											</Stack>
										</TableDataCell>
										<TableDataCell textAlign="center">
											{isTruthy(gstIncluded) ? (
												<Text>
													{globalTaxAppliedOn === "only_markup"
														? "Only Markup"
														: "Cost + Markup"}
												</Text>
											) : (
												<Text>-</Text>
											)}
										</TableDataCell>
										<TableDataCell textAlign="center">
											<ComponentTaxedValue
												costPriceCurrency={costPriceCurrency}
												costPrice={Number(price)}
												marginName={`options[${optionIndex}].margins.per_person_allocation.${id}`}
											/>
										</TableDataCell>
										<TableDataCell textAlign="right">
											<PriceWithTaxAndMargin
												costPriceCurrency={costPriceCurrency}
												costPrice={Number(price)}
												marginName={`options[${optionIndex}].margins.per_person_allocation.${id}`}
											/>
										</TableDataCell>
										{givenPriceCurrency &&
										givenPriceCurrency !== costPriceCurrency ? (
											<TableDataCell textAlign="right">
												<PriceWithTaxAndMarginInGivenCurrency
													costPriceCurrency={costPriceCurrency}
													costPrice={Number(price)}
													marginName={`options[${optionIndex}].margins.per_person_allocation.${id}`}
													givenPriceCurrency={givenPriceCurrency}
												/>
											</TableDataCell>
										) : null}
										<TableDataCell textAlign="right" borderLeftWidth="1">
											<RoundedComponentPriceWithTaxAndMarginInGivenCurrency
												costPriceCurrency={costPriceCurrency}
												costPrice={Number(price)}
												marginName={`options[${optionIndex}].margins.per_person_allocation.${id}`}
												roundingName={`options[${optionRoundingPointIndex}].margins.rounding_point`}
												givenPriceCurrency={givenPriceCurrency}
											/>
											<Text color="muted" fontSize="sm">
												x {pax_count}
											</Text>
										</TableDataCell>
									</tr>
								)
						)}
					</tbody>
				) : null}
				<tbody>
					<tr
						style={{
							fontStyle: "italic",
						}}
					>
						<TableDataCell
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
							fontWeight="semibold"
						>
							{subTotalRowLabel}
						</TableDataCell>
						<TableDataCell
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
							textAlign="right"
							fontWeight="semibold"
						>
							<Money
								currency={costPriceCurrency}
								amount={costPriceWithoutFlights}
							/>
						</TableDataCell>
						<TableDataCell
							textAlign="center"
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
						>
							<Stack alignItems="center">
								<TotalMarginInputField
									optionIndex={optionIndex}
									name={`options[${optionIndex}].margins.total`}
									costPrice={costPriceWithoutFlights}
									hasPerPersonPrices={hasPerPersonPrices}
									disabled={readOnlyMode}
								/>
							</Stack>
						</TableDataCell>
						<TableDataCell
							textAlign="center"
							fontWeight="semibold"
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
						>
							{isTruthy(gstIncluded) ? (
								<Text>
									{globalTaxAppliedOn === "only_markup"
										? "Only Markup"
										: "Cost + Markup"}
								</Text>
							) : (
								<Text>-</Text>
							)}
						</TableDataCell>
						<TableDataCell
							textAlign="center"
							fontWeight="semibold"
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
						>
							<ComponentTaxedValue
								costPriceCurrency={costPriceCurrency}
								costPrice={costPriceWithoutFlights}
								marginName={`options[${optionIndex}].margins.total`}
							/>
						</TableDataCell>
						<TableDataCell
							textAlign="right"
							fontWeight="semibold"
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
						>
							<PriceWithTaxAndMargin
								costPriceCurrency={costPriceCurrency}
								costPrice={costPriceWithoutFlights}
								marginName={`options[${optionIndex}].margins.total`}
							/>
							<Text fontWeight="normal" color="muted" fontSize="sm">
								{costPriceCurrency}
							</Text>
						</TableDataCell>
						{givenPriceCurrency && givenPriceCurrency !== costPriceCurrency ? (
							<TableDataCell
								textAlign="right"
								fontWeight="semibold"
								bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
							>
								<PriceWithTaxAndMarginInGivenCurrency
									costPriceCurrency={costPriceCurrency}
									costPrice={costPriceWithoutFlights}
									marginName={`options[${optionIndex}].margins.total`}
									givenPriceCurrency={givenPriceCurrency}
								/>
								<Text fontWeight="normal" color="muted" fontSize="sm">
									{givenPriceCurrency}
								</Text>
							</TableDataCell>
						) : null}
						<TableDataCell
							textAlign="right"
							fontWeight="semibold"
							borderLeftWidth="1"
							bgColor={subTotalRowLabel === "Sub-Total" ? "inset" : "warning"}
						>
							{/* flightAllocations: enables sum per-pax rounded amounts instead of rounding the aggregate flight total */}
							<PackagePriceWithoutFlightPrice
								costPriceCurrency={costPriceCurrency}
								givenPriceCurrency={givenPriceCurrency}
								optionIndex={optionIndex}
								flightPrice={flightPriceInCostingCurrency}
								flightCostPriceInCostingCurrency={
									flightCostPriceInCostingCurrency || 0
								}
								enableFlightRounding={enableFlightRounding}
								flightAllocations={flightAllocations}
							/>
							<Text fontWeight="normal" color="muted" fontSize="sm">
								{givenPriceCurrency}
							</Text>
						</TableDataCell>
					</tr>
				</tbody>
				{/*
				 * Flight Row Component with advanced taxation features and conditional
				 * rounding support (via `enableFlightRounding`) for V2 pricing strategies.
				 * Renders both the per-passenger allocation rows (when enabled) and the
				 * aggregate flight pricing row.
				 */}
				{showFlightsSection ? (
					<FlightsComponentRow
						optionIndex={optionIndex}
						costPriceCurrency={costPriceCurrency}
						givenPriceCurrency={givenPriceCurrency}
						flightCostPriceInCostingCurrency={Number(
							flightCostPriceInCostingCurrency || 0
						)}
						flightPriceInCostingCurrency={flightPriceInCostingCurrency}
						enableFlightRounding={enableFlightRounding}
						globalTaxEnabled={globalTaxEnabled}
						flightAllocations={flightAllocations}
						readOnly={readOnlyMode}
					/>
				) : null}
				{showFlightsSection ? (
					<TotalSummarRowForV1Strategies
						optionIndex={optionIndex}
						hasPerPersonCostPrices={hasPerPersonPrices}
						costPriceWithoutFlights={costPriceWithoutFlights}
						flightPriceInCostingCurrency={flightPriceInCostingCurrency}
						flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency}
						enableFlightRounding={enableFlightRounding}
					/>
				) : null}
			</Table>
			<WarnForSignificantRoundingIncrement
				costPriceCurrency={costPriceCurrency}
				givenPriceCurrency={givenPriceCurrency}
				optionIndex={optionIndex}
				costPrice={costPriceWithoutFlights}
				flightPrice={flightPriceInCostingCurrency}
				flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency || 0}
				enableFlightRounding={enableFlightRounding}
				marginName={`options[${optionIndex}].margins.total`}
				roundingName={`options[${optionRoundingPointIndex}].margins.rounding_point`}
				flightAllocations={flightAllocations}
			/>
			<DeferRender by={500} noLoader>
				<SyncPackagePrice
					optionIndex={optionIndex}
					hasPerPersonCostPrices={hasPerPersonPrices}
					costPriceWithoutFlights={costPriceWithoutFlights}
					flightPriceInCostingCurrency={flightPriceInCostingCurrency}
					perPersonCostPriceAllocation={perPersonCostPriceAllocation}
					flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency}
					enableFlightRounding={enableFlightRounding}
				/>
				<SyncPerPersonMarginsWithTotalMargin
					optionIndex={optionIndex}
					costPriceCurrency={costPriceCurrency}
					givenPriceCurrency={givenPriceCurrency}
					costPriceWithoutFlights={costPriceWithoutFlights}
					flightPriceInCostingCurrency={flightPriceInCostingCurrency}
					flightCostPriceInCostingCurrency={
						flightCostPriceInCostingCurrency || 0
					}
					enableFlightRounding={enableFlightRounding}
					perPersonCostPriceAllocation={perPersonCostPriceAllocation}
					hasPerPersonCostPrices={hasPerPersonPrices}
				/>
				<SyncCalculatedValuesForStrategiesV1
					optionIndex={optionIndex}
					hasPerPersonCostPrices={hasPerPersonPrices}
					quoteComponentPrices={quoteComponentPrices}
					enableFlightRounding={enableFlightRounding}
					perPersonCostPriceAllocation={perPersonCostPriceAllocation}
				/>
			</DeferRender>
		</Stack>
	)
}

function PerPersonMarginInputField({
	optionIndex,
	name,
	costPrice,
	disabled,
}: {
	optionIndex: number
	name: string
	costPrice: number
	disabled?: boolean
}) {
	const { value: _use_percentage } = useFieldValue(
		`options[${optionIndex}].margins.use_percentage`
	)
	const use_percentage = isTruthy(_use_percentage)
	const { value: _editing_total, onChange: changeEditingTotal } = useFieldValue(
		`options[${optionIndex}].margins.editing_total`
	)
	const editing_total = isTruthy(_editing_total)
	const { value: margin, onChange: changeMargin } = useFieldValue<
		number | undefined | ""
	>(name)
	const marginPercentageName = name + "_percentage"
	const { value: marginPercentage } = useFieldValue<number | undefined | "">(
		marginPercentageName
	)
	const readOnlyForAbsolute = editing_total

	useDidMount(() => {
		if (!use_percentage) {
			// set the margin if we have margin percentage and absolute are not set
			if (
				!readOnlyForAbsolute &&
				marginPercentage &&
				(margin === undefined || margin === null || margin === "")
			) {
				changeMargin(
					normalizeNumber(
						(Number(marginPercentage || 0) * Number(costPrice || 0)) / 100,
						2
					)
				)
			}
		}
	})

	if (isTruthy(use_percentage)) {
		return (
			<ComponentPercentageMarginInputField
				name={name}
				costPrice={costPrice}
				readOnly
			/>
		)
	}
	return (
		<Inline alignItems="center" gap="2">
			<ComponentAbsoluteMarginInputField
				name={name}
				costPrice={costPrice}
				readOnly={disabled || readOnlyForAbsolute}
			/>
			{editing_total ? (
				<Box opacity="50">
					<Button
						size="sm"
						level="tertiary"
						disabled={disabled}
						onClick={() => {
							changeEditingTotal(editing_total ? 0 : 1)
						}}
					>
						<Icons.Pencil />
					</Button>
				</Box>
			) : null}
		</Inline>
	)
}

function TotalMarginInputField({
	optionIndex,
	name,
	costPrice,
	hasPerPersonPrices,
	disabled,
}: {
	optionIndex: number
	name: string
	costPrice: number
	hasPerPersonPrices: boolean
	disabled?: boolean
}) {
	const { value: _use_percentage } = useFieldValue(
		`options[${optionIndex}].margins.use_percentage`
	)
	const use_percentage = isTruthy(_use_percentage)
	const { value: _editing_total, onChange: changeEditingTotal } = useFieldValue(
		`options[${optionIndex}].margins.editing_total`
	)
	const editing_total = isTruthy(_editing_total)
	const { value: margin, onChange: changeMargin } = useFieldValue<
		number | undefined | ""
	>(name)
	const marginPercentageName = name + "_percentage"
	const { value: marginPercentage, onChange: changeMarginPercentage } =
		useFieldValue<number | undefined | "">(marginPercentageName)

	useDidMount(() => {
		if (use_percentage) {
			// set the margin percentage if we have absolute margin and percentage are not set
			if (
				margin &&
				(marginPercentage === undefined ||
					marginPercentage === null ||
					marginPercentage === "")
			) {
				changeMarginPercentage(
					normalizeNumber(
						(Number(margin || 0) * 100) / Number(costPrice || 1),
						4
					)
				)
			}
		} else {
			// set the margin if we have margin percentage and absolute are not set
			if (
				!hasPerPersonPrices &&
				marginPercentage &&
				(margin === undefined || margin === null || margin === "")
			) {
				changeMargin(
					normalizeNumber(
						(Number(marginPercentage || 0) * Number(costPrice || 0)) / 100,
						2
					)
				)
			}
		}
	})

	if (isTruthy(use_percentage)) {
		return (
			<ComponentPercentageMarginInputField
				name={name}
				costPrice={costPrice}
				readOnly={disabled}
			/>
		)
	}

	return (
		<Inline alignItems="center" gap="2">
			<ComponentAbsoluteMarginInputField
				name={name}
				costPrice={costPrice}
				readOnly={disabled || (!editing_total && hasPerPersonPrices)}
				showPercentage
			/>
			{!editing_total && hasPerPersonPrices ? (
				<Button
					size="sm"
					level="tertiary"
					disabled={disabled}
					onClick={() => {
						changeEditingTotal(editing_total ? 0 : 1)
					}}
				>
					<Icons.Pencil />
				</Button>
			) : null}
		</Inline>
	)
}

function ComponentAbsoluteMarginInputField({
	name,
	costPrice,
	readOnly,
	showPercentage,
}: {
	name: string
	costPrice: number
	readOnly?: boolean
	showPercentage?: boolean
}) {
	const { value: margin } = useFieldValue<number | undefined | "">(name)
	const marginPercentageName = name + "_percentage"
	const { value: marginPercentage, onChange: changeMarginPercentage } =
		useFieldValue<number | undefined | "">(marginPercentageName)

	// update the margin percentage if cost or percentage  changes
	useEffect(() => {
		// NOTE: We are using the timer as other components depends on this updated value
		// TODO: Remove this setTimeout
		const h = setTimeout(() => {
			changeMarginPercentage(
				normalizeNumber((Number(margin || 0) * 100) / Number(costPrice || 1), 4)
			)
		}, 100)
		return () => clearTimeout(h)
	}, [changeMarginPercentage, margin, costPrice])

	if (readOnly) {
		return <Text>{numberToLocalString(Number(margin || 0), 0)}</Text>
	}

	return (
		<TextInputField
			type="number"
			name={name}
			style={{ width: "100px" }}
			size="sm"
			min={0}
			help={
				showPercentage
					? `= ${normalizeNumber(Number(marginPercentage || 0), 2)}%`
					: null
			}
		/>
	)
}

function ComponentPercentageMarginInputField({
	name,
	costPrice,
	readOnly,
}: {
	name: string
	costPrice: number
	readOnly?: boolean
}) {
	const { value: margin, onChange: changeMargin } = useFieldValue<
		number | undefined | ""
	>(name)
	const marginPercentageName = name + "_percentage"
	const { value: marginPercentage } = useFieldValue<number | undefined | "">(
		marginPercentageName
	)

	// update the margin if cost or percentage  changes
	useEffect(() => {
		if (marginPercentage !== undefined && marginPercentage !== "") {
			// NOTE: We are using the timer as other components depends on this updated value
			// TODO: Remove this timer
			const h = setTimeout(() => {
				changeMargin(
					normalizeNumber(
						(Number(marginPercentage || 0) * Number(costPrice || 0)) / 100
					)
				)
			}, 100)
			return () => clearTimeout(h)
		}
	}, [changeMargin, marginPercentage, costPrice])

	if (readOnly) {
		return <Text>{numberToLocalString(Number(margin || 0), 0)}</Text>
	}

	return (
		<PercentageInputField
			name={marginPercentageName}
			help={`= ${numberToLocalString(Number(margin || 0), 0)}`}
			size="sm"
		/>
	)
}

function SyncPerPersonMarginsWithTotalMargin({
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	enableFlightRounding = false,
	perPersonCostPriceAllocation,
	hasPerPersonCostPrices,
}: {
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency: number
	enableFlightRounding?: boolean
	perPersonCostPriceAllocation: ITripQuote["cost_prices_per_person"]
	hasPerPersonCostPrices: boolean
}) {
	const name = `options[${optionIndex}].margins`
	const { value: margins } =
		useFieldValue<IGiveQuoteData["options"][number]["margins"]>(name)
	const form = useForm()
	const use_percentage = isTruthy(margins?.use_percentage)
	const editing_total = isTruthy(margins?.editing_total)
	const totalMargin = margins?.total
	const totalMarginPercentage = margins?.total_percentage
	const perPersonMarginAllocaton = margins?.per_person_allocation

	// now costprice allocations are seperated here for margin and tax are calculated differntly on both
	const {
		flight: flightCostPriceAllocation = [],
		accommodation: accommodationCostpriceAllocation = [],
	} = useMemo(
		() => partitionPerPersonAllocations(perPersonCostPriceAllocation || []),
		[perPersonCostPriceAllocation]
	)

	// Per-pax flight markup/tax computed from each pax type's own given price,
	// so the per-person split matches the true per-pax markup (each pax type has
	// an independent selling price) instead of a blended pro-rata distribution.
	const { allocationResults: flightAllocationResults } =
		useFlightAllocationsPricing({
			optionIndex,
			costPriceCurrency,
			givenPriceCurrency,
			flightCostPriceInCostingCurrency,
			flightPriceInCostingCurrency,
			flightAllocations: flightCostPriceAllocation,
			enableFlightRounding,
		})

	// sync the percentage per-person with total
	// service: accommodation (non-flight)
	useEffect(() => {
		if (use_percentage && hasPerPersonCostPrices) {
			const h = setTimeout(() => {
				form.batch(() => {
					accommodationCostpriceAllocation.forEach(({ id, price }) => {
						form.change(
							`${name}.per_person_allocation.${id}_percentage`,
							Number(price || 0) ? totalMarginPercentage : 0
						)
					})
				})
			}, 100)
			return () => clearTimeout(h)
		}
	}, [
		form,
		name,
		costPriceWithoutFlights,
		totalMarginPercentage,
		accommodationCostpriceAllocation,
		hasPerPersonCostPrices,
		use_percentage,
	])

	// sync the per-person with total if EDITING total (absolute) and per-person are set
	// service: accommodation (non-flight)
	useEffect(() => {
		if (editing_total && !use_percentage && hasPerPersonCostPrices) {
			const factor =
				Number(totalMargin || 0) / (Number(costPriceWithoutFlights) || 1)
			const h = setTimeout(() => {
				form.batch(() => {
					accommodationCostpriceAllocation.forEach(({ id, price }) => {
						form.change(
							`${name}.per_person_allocation.${id}`,
							Number(price || 0) ? Number(price || 0) * factor : 0
						)
					})
				})
			}, 100)
			return () => clearTimeout(h)
		}
	}, [
		form,
		name,
		costPriceWithoutFlights,
		totalMargin,
		accommodationCostpriceAllocation,
		hasPerPersonCostPrices,
		editing_total,
		use_percentage,
	])

	// sync the total with per-person if NOT EDITING total (absolute) and per-person is set
	// service: accommodation (non-flight)
	useEffect(() => {
		if (!editing_total && !use_percentage && hasPerPersonCostPrices) {
			const h = setTimeout(() => {
				form.change(
					`${name}.total`,
					accommodationCostpriceAllocation.reduce<number>(
						(total, { id, pax_count, price }) => {
							return (
								total +
								(Number(price || 0)
									? Number(perPersonMarginAllocaton?.[id] || 0) * pax_count
									: 0)
							)
						},
						0
					)
				)
			}, 100)
			return () => clearTimeout(h)
		}
	}, [
		form,
		name,
		editing_total,
		use_percentage,
		accommodationCostpriceAllocation,
		perPersonMarginAllocaton,
		hasPerPersonCostPrices,
	])

	// sync per-pax flight margins into per_person_allocation
	// service: flight
	useEffect(() => {
		if (!hasPerPersonCostPrices || !flightCostPriceAllocation.length) return
		const h = setTimeout(() => {
			form.batch(() => {
				flightCostPriceAllocation.forEach(({ id, price }, i) => {
					const perPaxCP = Number(price || 0)
					const result = flightAllocationResults[i]
					const markup = result
						? Number(formatMoneyByDecimal(result.markup) || 0)
						: 0
					form.change(
						`${name}.per_person_allocation.${id}`,
						perPaxCP > 0 ? markup : 0
					)
					form.change(
						`${name}.per_person_allocation.${id}_percentage`,
						perPaxCP > 0 ? Number(result?.markupPercentage || 0) : 0
					)
				})
			})
		}, 100)
		return () => clearTimeout(h)
	}, [
		form,
		name,
		flightCostPriceAllocation,
		flightAllocationResults,
		hasPerPersonCostPrices,
	])

	return null
}

/**
 * This hook is specifically designed for handling the calculation of markup and tax for the “flights” component in old pricing strategy.
 */
function useFlightPriceCalculation({
	optionIndex,
	flightCostPrice,
	flightGivenPrice,
	enableRounding = false,
}: {
	optionIndex: number
	flightCostPrice: number
	flightGivenPrice: number
	enableRounding?: boolean
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")

	const { gstIncluded, taxPercentage, taxAppliedOn, rounding, exchangeRate } =
		useFlightPricingConfig({
			optionIndex,
			costPriceCurrency,
			givenPriceCurrency,
		})

	return useMemo(() => {
		return getFlightComponentPriceCalculationValues({
			costPrice: moneyParseByDecimal(flightCostPrice, costPriceCurrency),
			givenPriceWithTax: moneyParseByDecimal(
				flightGivenPrice,
				costPriceCurrency
			),
			taxPercentage,
			taxIncluded: gstIncluded,
			taxAppliedOn,
			currency: costPriceCurrency,
			givenCurrency: givenPriceCurrency,
			exchangeRate,
			rounding: enableRounding ? rounding : undefined,
		})
	}, [
		flightCostPrice,
		flightGivenPrice,
		taxPercentage,
		taxAppliedOn,
		gstIncluded,
		costPriceCurrency,
		givenPriceCurrency,
		rounding,
		enableRounding,
		exchangeRate,
	])
}

/**
 * Total Calculations Hook for Old V1/V2 Pricing Strategies
 *
 * This hook provides total calculations for old pricing strategies
 * (overall, per_person) by combining subtotal and flight values.
 *
 */
function useTotalCalculationsForV1Strategies({
	optionIndex,
	hasPerPersonCostPrices,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	enableFlightRounding = false,
}: {
	optionIndex: number
	hasPerPersonCostPrices: boolean
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency?: number
	enableFlightRounding?: boolean
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { value: givenPrice } = useFieldValue<
		IGiveQuoteData["options"][number]["given_price"]
	>(`options[${optionIndex}].given_price`)
	const { value: enableSyncRoundingAmongOptions } = useFieldValue<
		0 | 1 | undefined
	>("enable_sync_rounding_among_options")
	const { value: gstIncluded } = useFieldValue<boolean>(
		"options[0].gst_included"
	)
	const { value: taxPercentage } = useFieldValue<number | null | undefined>(
		"options[0].taxes.total_percentage"
	)
	const { value: taxAppliedOn } = useFieldValue<string>(
		"options[0].taxes.total_applied_on"
	)
	const { value: totalMargin } = useFieldValue<number | undefined>(
		`options[${optionIndex}].margins.total`
	)
	const optionRoundingPointIndex = enableSyncRoundingAmongOptions
		? 0
		: optionIndex
	const { value: roundingPoint } = useFieldValue<number | undefined>(
		`options[${optionRoundingPointIndex}].margins.rounding_point`
	)

	// Per-person pricing inputs (mirrors SyncPackagePrice behavior)
	const isCostPriceCurrencySameAsGivenPriceCurrency =
		costPriceCurrency === givenPriceCurrency

	const { value: perPersonCostPriceAllocation } = useFieldValue<
		Array<TPricePerPersonAllocation> | undefined
	>(`options[${optionIndex}].cost_prices_per_person`)

	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			givenPriceCurrency
		)

	const costPriceToGivenPriceExchangeRate = costPriceGivenPriceCurrencyPairRatio
		? costPriceGivenPriceCurrencyPairRatio
		: 1

	// Get flight calculations
	const flightResults = useFlightPriceCalculation({
		optionIndex,
		flightCostPrice: flightCostPriceInCostingCurrency || 0,
		flightGivenPrice: flightPriceInCostingCurrency,
		enableRounding: enableFlightRounding,
	})

	// Allocation-aware flight rounded total (sum of per-pax rounded selling
	// prices). `roundedInGiven` is null when there are no per-person flight
	// allocations, in which case we fall back to the aggregate flight rounding
	// below — keeping behaviour identical for non-per-person quotes.
	const { flight: flightAllocations = [] } = useMemo(
		() => partitionPerPersonAllocations(perPersonCostPriceAllocation || []),
		[perPersonCostPriceAllocation]
	)

	const {
		flightAllocationTotals: { roundedInGiven: flightAllocationRoundedTotal },
	} = useFlightAllocationsPricing({
		optionIndex,
		costPriceCurrency,
		givenPriceCurrency,
		flightCostPriceInCostingCurrency: Number(
			flightCostPriceInCostingCurrency || 0
		),
		flightPriceInCostingCurrency: Number(flightPriceInCostingCurrency || 0),
		flightAllocations,
		enableFlightRounding,
	})

	//Note - There is a critical difference for "Sub Total Prices Caluculation for Per Person Pricing Strategy" between Old (Per Person V1/V2) and New (Per Component Per Person V2) pricing strategy which is in the new pricing strategy
	//for subtotal calculation we use method of calculation from per person row, means for example if we need to calculate subtotal tax, then we sum individual per person allocated tax but in the old pricing strategy (overall/PerPerson V1/V2), subtotal tax will be calculated
	//using total cost price, total markup, global tax percentage. The reason we're are mainting this method for old pricing strategy is because of backward compatibility for old quotes as new pricing strategies are introduced after more than 5 years of old pricing strategies,
	//so old quotes created with these old pricing stratgies will be consistent.

	return useMemo(() => {
		const hasPerPersonAllocations =
			Boolean(
				perPersonCostPriceAllocation?.length &&
				perPersonCostPriceAllocation.some((a) => Number(a.price) > 0)
			) && hasPerPersonCostPrices

		const subtotalCostPrice = moneyParseByDecimal(
			costPriceWithoutFlights,
			costPriceCurrency
		)

		const subtotalMarkup = moneyParseByDecimal(
			Number(totalMargin || 0),
			costPriceCurrency
		)

		// Calculate subtotal tax based on tax_applied_on setting
		const appliedOn = taxAppliedOn || "cost_plus_markup"
		const tax = isTruthy(gstIncluded) && taxPercentage ? taxPercentage : 0
		let subtotalTaxBase: TMoney

		if (appliedOn === "only_markup") {
			subtotalTaxBase = subtotalMarkup
		} else {
			subtotalTaxBase = addMoney(subtotalCostPrice, subtotalMarkup)
		}

		let subtotalTax = moneyParseByDecimal(0, costPriceCurrency)

		if (tax) {
			if (subtotalTaxBase.amount > 0) {
				subtotalTax = getPercentageOfMoney(
					subtotalTaxBase,
					Number(taxPercentage)
				)
			}
		}

		//subtotalUnroundedInCostingCurrency
		const subtotalUnroundedInCostingCurrency = addMoney(
			subtotalCostPrice,
			subtotalMarkup,
			subtotalTax
		)

		//flight cost price in costing currency
		const flightCostPrice = moneyParseByDecimal(
			Number(flightCostPriceInCostingCurrency || 0),
			costPriceCurrency
		)

		// Prefer the allocation-aware rounded flight total (sum of per-pax rounded
		// selling prices) so flights match the per-person rows like every other
		// component; fall back to the aggregate rounding when no flight
		// allocations exist.
		const flightRoundedInGiven =
			flightAllocationRoundedTotal ??
			flightResults?.roundedInGiven ??
			moneyParseByDecimal(0, givenPriceCurrency || costPriceCurrency)

		const totalCostPrice = addMoney(subtotalCostPrice, flightCostPrice)

		const totalMarkup = addMoney(
			subtotalMarkup,
			flightResults?.markup || moneyParseByDecimal(0, costPriceCurrency)
		)

		const totalTax = addMoney(
			subtotalTax,
			flightResults?.tax || moneyParseByDecimal(0, costPriceCurrency)
		)

		const subtotalUnroundedInGivenCurrency =
			!isCostPriceCurrencySameAsGivenPriceCurrency
				? convertMoneyToCurrency(
						subtotalUnroundedInCostingCurrency,
						givenPriceCurrency,
						costPriceToGivenPriceExchangeRate
					)
				: subtotalUnroundedInCostingCurrency

		let subtotalRoundedInGivenCurrency = roundingPoint
			? roundMoneyToPoint(subtotalUnroundedInGivenCurrency, roundingPoint)
			: subtotalUnroundedInGivenCurrency

		const totalUnroundedInCostingCurrency = addMoney(
			subtotalUnroundedInCostingCurrency,
			flightResults?.unrounded || moneyParseByDecimal(0, costPriceCurrency)
		)

		const totalUnroundedInGivenCurrency = addMoney(
			subtotalUnroundedInGivenCurrency,
			flightResults?.unroundedInGiven ||
				moneyParseByDecimal(0, givenPriceCurrency || costPriceCurrency)
		)

		//Total Rounded Price in Given Currency For Old Overall Pricing Strategy
		let totalRoundedInGivenCurrency = addMoney(
			subtotalRoundedInGivenCurrency,
			flightRoundedInGiven
		)

		if (hasPerPersonAllocations && perPersonCostPriceAllocation) {
			//This flow sync the behaviour we have for old (Per Person) Pricing Strategy in which Subtotal Rounded Given Price In Given Currency is Calculated
			//by adding each per person rounded price and then calculates the final given price (refer to "SyncPackagePrice" component for more info).

			const totalPackageGivenPrice = moneyParseByDecimal(
				Number(givenPrice || 0),
				givenPriceCurrency
			)
			subtotalRoundedInGivenCurrency = subtractMoney(
				totalPackageGivenPrice,
				flightRoundedInGiven
			)

			//Total Rounded Price in Given Currency For Per Person Strategy
			totalRoundedInGivenCurrency = totalPackageGivenPrice
		}

		return {
			// Subtotal values
			subtotal: {
				costPrice: subtotalCostPrice,
				markup: subtotalMarkup,
				tax: subtotalTax,
				unroundedInCostingCurrency: subtotalUnroundedInCostingCurrency,
				unroundedInGivenCurrency: subtotalUnroundedInGivenCurrency,
				roundedInGivenCurrency: subtotalRoundedInGivenCurrency,
			},
			// Flight values (from flight calculations)
			flights: {
				costPrice: flightCostPrice,
				markup:
					flightResults?.markup || moneyParseByDecimal(0, costPriceCurrency),
				marginPercentage: flightResults?.markupPercentage || 0,
				tax: flightResults?.tax || moneyParseByDecimal(0, costPriceCurrency),
				taxPercentage: flightResults?.taxPercentage || 0,
				unroundedInCostingCurrency:
					flightResults?.unrounded || moneyParseByDecimal(0, costPriceCurrency),
				unroundedInGivenCurrency:
					flightResults?.unroundedInGiven ||
					moneyParseByDecimal(0, givenPriceCurrency || costPriceCurrency),
				roundedInGivenCurrency: flightRoundedInGiven,
			},
			// Total values (subtotal + flight)
			total: {
				costPrice: totalCostPrice,
				markup: totalMarkup,
				tax: totalTax,
				unroundedInCostingCurrency: totalUnroundedInCostingCurrency,
				unroundedInGivenCurrency: totalUnroundedInGivenCurrency,
				roundedInGivenCurrency: totalRoundedInGivenCurrency,
			},
		}
	}, [
		costPriceWithoutFlights,
		costPriceCurrency,
		givenPriceCurrency,
		givenPrice,
		gstIncluded,
		taxPercentage,
		taxAppliedOn,
		totalMargin,
		roundingPoint,
		costPriceToGivenPriceExchangeRate,
		flightResults,
		flightAllocationRoundedTotal,
		flightCostPriceInCostingCurrency,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		hasPerPersonCostPrices,
		perPersonCostPriceAllocation,
	])
}

function useComponentPriceWithMargin({
	costPriceCurrency,
	costPrice,
	marginName,
}: {
	costPriceCurrency: string
	costPrice: number
	marginName: string
}) {
	const { value: margin } = useFieldValue<number | undefined>(marginName)
	const price = Number(costPrice) + Number(margin || 0)
	return moneyParseByDecimal(price, costPriceCurrency)
}

function useComponentTaxedValue({
	costPriceCurrency,
	costPrice,
	marginName,
}: {
	costPriceCurrency: string
	costPrice: number
	marginName: string
}) {
	const { value: gstIncluded } = useFieldValue<boolean>(
		`options[0].gst_included`
	)
	const { value: tax_percentage } = useFieldValue<number | null | undefined>(
		`options[0].taxes.total_percentage`
	)
	const { value: taxAppliedOn } = useFieldValue<string>(
		`options[0].taxes.total_applied_on`
	)
	const { value: margin } = useFieldValue<number | undefined>(marginName)

	const tax = isTruthy(gstIncluded) && tax_percentage ? tax_percentage : 0
	const appliedOn = taxAppliedOn || "cost_plus_markup" // Default for backward compatibility

	const priceWithMargin = useComponentPriceWithMargin({
		costPriceCurrency,
		costPrice,
		marginName,
	})

	if (!tax) return null

	let taxBase
	if (appliedOn === "only_markup") {
		// Calculate tax only on markup amount
		const marginAmount = Number(margin || 0)
		taxBase = moneyParseByDecimal(marginAmount, costPriceCurrency)
	} else {
		// Calculate tax on cost + markup
		taxBase = priceWithMargin
	}

	return taxBase.amount > 0
		? getPercentageOfMoney(taxBase, Number(tax_percentage))
		: createMoney(0, costPriceCurrency)
}

function ComponentTaxedValue({
	costPriceCurrency,
	costPrice,
	marginName,
}: {
	costPriceCurrency: string
	costPrice: number
	marginName: string
}) {
	const value = useComponentTaxedValue({
		costPriceCurrency,
		costPrice,
		marginName,
	})
	return <Text>{value ? <Money money={value} /> : "-"}</Text>
}

function useComponentPriceWithMarginAndTax({
	costPriceCurrency,
	costPrice,
	marginName,
}: {
	costPrice: number
	costPriceCurrency: string
	marginName: string
}) {
	const price = useComponentPriceWithMargin({
		costPriceCurrency,
		costPrice,
		marginName,
	})
	const taxValue = useComponentTaxedValue({
		costPrice,
		costPriceCurrency,
		marginName,
	})
	let money = price
	if (taxValue) {
		money = addMoney(price, taxValue)
	}
	return money
}

function PriceWithTaxAndMargin({
	costPriceCurrency,
	costPrice,
	marginName,
}: {
	costPrice: number
	costPriceCurrency: string
	marginName: string
}) {
	const money = useComponentPriceWithMarginAndTax({
		costPrice,
		costPriceCurrency,
		marginName,
	})
	return <Money money={money} />
}

function useComponentPriceWithMarginAndTaxInGivenCurrency({
	costPriceCurrency,
	costPrice,
	givenPriceCurrency,
	marginName,
}: {
	costPrice: number
	costPriceCurrency: string
	givenPriceCurrency: string
	marginName: string
}) {
	const money = useComponentPriceWithMarginAndTax({
		costPrice,
		costPriceCurrency,
		marginName,
	})
	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			givenPriceCurrency
		)

	return convertMoneyToCurrency(
		money,
		givenPriceCurrency,
		costPriceGivenPriceCurrencyPairRatio || 0
	)
}

function PriceWithTaxAndMarginInGivenCurrency({
	costPriceCurrency,
	costPrice,
	marginName,
	givenPriceCurrency,
}: {
	costPrice: number
	costPriceCurrency: string
	marginName: string
	givenPriceCurrency: string
}) {
	const money = useComponentPriceWithMarginAndTaxInGivenCurrency({
		costPrice,
		costPriceCurrency,
		marginName,
		givenPriceCurrency,
	})

	return <Money money={money} />
}

function RoundedComponentPriceWithTaxAndMarginInGivenCurrency({
	costPriceCurrency,
	costPrice,
	marginName,
	givenPriceCurrency,
	roundingName,
}: {
	costPrice: number
	costPriceCurrency: string
	marginName: string
	givenPriceCurrency: string
	roundingName: string
}) {
	const money = useComponentPriceWithMarginAndTaxInGivenCurrency({
		costPrice,
		costPriceCurrency,
		marginName,
		givenPriceCurrency,
	})

	const { value: roundingPoint } = useFieldValue<number | undefined>(
		roundingName
	)

	const roundedMoney = roundMoneyToPoint(money, roundingPoint)

	return <Money money={roundedMoney} />
}

function usePackagePriceWithoutFlightPrice({
	flightPrice: flightGivenPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	enableFlightRounding,
	flightAllocations,
}: {
	flightPrice: number
	flightCostPriceInCostingCurrency: number
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	enableFlightRounding?: boolean
	flightAllocations?: TPricePerPersonAllocation[]
}) {
	const { value: packagePriceInGivenCurrency } = useFieldValue<
		IGiveQuoteData["options"][number]["given_price"]
	>(`options[${optionIndex}].given_price`)

	const flightResults = useFlightPriceCalculation({
		optionIndex,
		flightCostPrice: flightCostPriceInCostingCurrency,
		flightGivenPrice: flightGivenPriceInCostingCurrency,
		enableRounding: enableFlightRounding,
	})

	// flight total repsect to per person rounding
	const { flightAllocationTotals } = useFlightAllocationsPricing({
		optionIndex,
		costPriceCurrency,
		givenPriceCurrency,
		flightCostPriceInCostingCurrency,
		flightPriceInCostingCurrency: flightGivenPriceInCostingCurrency,
		flightAllocations: flightAllocations || [],
		enableFlightRounding: enableFlightRounding || false,
	})

	const flightPriceInGivenCurrency =
		flightAllocationTotals.roundedInGiven ?? flightResults.roundedInGiven

	return subtractMoney(
		moneyParseByDecimal(packagePriceInGivenCurrency, givenPriceCurrency),
		flightPriceInGivenCurrency
	)
}

function PackagePriceWithoutFlightPrice({
	flightPrice,
	flightCostPriceInCostingCurrency,
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	enableFlightRounding,
	flightAllocations,
}: {
	flightPrice: number
	flightCostPriceInCostingCurrency: number
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	enableFlightRounding?: boolean
	flightAllocations?: TPricePerPersonAllocation[]
}) {
	const price = usePackagePriceWithoutFlightPrice({
		flightPrice,
		flightCostPriceInCostingCurrency,
		optionIndex,
		costPriceCurrency,
		givenPriceCurrency,
		enableFlightRounding,
		flightAllocations,
	})

	return <Money money={price} />
}

export function WarnForSignificantRoundingIncrement({
	costPrice,
	flightPrice,
	flightCostPriceInCostingCurrency,
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	marginName,
	roundingName,
	enableFlightRounding,
	flightAllocations,
}: {
	costPrice: number
	flightPrice: number
	flightCostPriceInCostingCurrency: number
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	marginName: string
	roundingName: string
	enableFlightRounding?: boolean
	flightAllocations?: TPricePerPersonAllocation[]
}) {
	// all prices are excluding flight price
	const totalGivenPriceBeforeRounding =
		useComponentPriceWithMarginAndTaxInGivenCurrency({
			costPrice,
			costPriceCurrency,
			marginName,
			givenPriceCurrency,
		})
	const totalGivenPriceAfterRounding = usePackagePriceWithoutFlightPrice({
		flightPrice,
		flightCostPriceInCostingCurrency,
		optionIndex,
		costPriceCurrency,
		givenPriceCurrency,
		enableFlightRounding,
		flightAllocations,
	})
	const { value: roundingPoint } = useFieldValue<number | undefined>(
		roundingName
	)

	const percentageIncrement =
		((totalGivenPriceAfterRounding.amount -
			totalGivenPriceBeforeRounding.amount) *
			100) /
		(totalGivenPriceBeforeRounding.amount || 1)
	if (percentageIncrement < 1) {
		return null
	}

	return (
		<Alert status="warning">
			<Stack gap="2">
				<Text fontSize="md" fontWeight="semibold">
					Possible High Rounding ({Number(percentageIncrement).toFixed(2)}%
					increased)
				</Text>
				<Text>
					The rounding point ({roundingPoint}) has increased the price by{" "}
					<b>
						{Number(percentageIncrement).toFixed(2)}% from{" "}
						<Money money={totalGivenPriceBeforeRounding} showCurrency /> to{" "}
						<Money money={totalGivenPriceAfterRounding} showCurrency />
					</b>
					.
				</Text>
				<Text>Please reconfirm or reduce the rounding if incorrect.</Text>
			</Stack>
		</Alert>
	)
}

/**
 * Wrapper to render the Total row for old strategies
 *
 * Handles total calculations + rendering
 */
function TotalSummarRowForV1Strategies({
	optionIndex,
	hasPerPersonCostPrices,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
	enableFlightRounding,
}: {
	optionIndex: number
	hasPerPersonCostPrices: boolean
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency?: number
	enableFlightRounding?: boolean
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")

	const totalCalculations = useTotalCalculationsForV1Strategies({
		optionIndex,
		hasPerPersonCostPrices,
		costPriceWithoutFlights,
		flightPriceInCostingCurrency,
		flightCostPriceInCostingCurrency,
		enableFlightRounding,
	})

	return (
		<>
			<tbody>
				<tr>
					<TableDataCell bgColor="warning" fontWeight="semibold">
						<Inline gap="2" alignItems="center">
							<Text>Total</Text>
						</Inline>
					</TableDataCell>
					<TableDataCell
						textAlign="right"
						fontWeight="semibold"
						bgColor="warning"
					>
						<Money money={totalCalculations.total.costPrice} />
					</TableDataCell>
					<TableDataCell textAlign="center" bgColor="warning">
						<Money money={totalCalculations.total.markup} />
					</TableDataCell>
					<TableDataCell textAlign="center" bgColor="warning"></TableDataCell>
					<TableDataCell
						textAlign="center"
						fontWeight="semibold"
						bgColor="warning"
					>
						<Money money={totalCalculations.total.tax} />
					</TableDataCell>
					<TableDataCell
						textAlign="right"
						fontWeight="semibold"
						bgColor="warning"
					>
						<Money
							money={totalCalculations.total.unroundedInCostingCurrency}
							showCurrency
						/>
					</TableDataCell>
					{givenPriceCurrency && givenPriceCurrency !== costPriceCurrency ? (
						<TableDataCell
							textAlign="right"
							fontWeight="semibold"
							bgColor="warning"
						>
							<Money
								money={totalCalculations.total.unroundedInGivenCurrency}
								showCurrency
							/>
						</TableDataCell>
					) : null}
					<TableDataCell
						textAlign="right"
						fontWeight="semibold"
						borderLeftWidth="1"
						bgColor="warning"
					>
						<Money
							money={totalCalculations.total.roundedInGivenCurrency}
							showCurrency
						/>
					</TableDataCell>
				</tr>
			</tbody>
		</>
	)
}

// To show final Preview table (per person / aggregate)
export function PackagePricePreview({
	optionIndex,
	flightGivenPriceInCostingCurrency,
	flightCostPriceInCostingCurrency,
}: {
	optionIndex: number
	flightGivenPriceInCostingCurrency: number
	flightCostPriceInCostingCurrency: number
}) {
	const { value: complimentaryCount } =
		useFieldValue<number>(`complimentary_count`)
	const { value: noOfAdults } = useFieldValue<number>(`no_of_adults`)
	const { value: children } = useFieldValue<TChildrenArray | undefined>(
		`children`
	)
	const { value: taxName } = useFieldValue<string>("tax_name")
	const { value: givenPricesPerPerson } = useFieldValue<
		IGiveQuoteData["options"][number]["given_prices_per_person"]
	>(`options[${optionIndex}].given_prices_per_person`)
	const { value: packagePriceInGivenCurrency } = useFieldValue<
		IGiveQuoteData["options"][number]["given_price"]
	>(`options[${optionIndex}].given_price`)
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { value: pricingStrategy } =
		useFieldValue<TGivenQuotePricingStrategy>("pricing_strategy")
	const { value: enableFlightRounding } = useFieldValue<0 | 1 | undefined>(
		"enable_flight_rounding"
	)

	const isPerComponentV2PricingStrategies =
		pricingStrategy === "per_component_v2" ||
		pricingStrategy === "per_component_per_person_v2"

	const flightResults = useFlightPriceCalculation({
		optionIndex,
		flightCostPrice: flightCostPriceInCostingCurrency,
		flightGivenPrice: flightGivenPriceInCostingCurrency,
		enableRounding:
			isTruthy(enableFlightRounding) || isPerComponentV2PricingStrategies, //Advance per component pricing strategies will always have flight rounded in the calculation, but for other strategies it should be decided by only the "enable_flight_rounding" (which is handled by the "usePricingStrategyMigrationSync" hook which decided whether to enable flight rounding or not)
	})

	const { value: existingGivenPriceCurrency } = useFieldValue<
		IGiveQuoteData["options"][number]["existing_given_price_currency"]
	>(`options[${optionIndex}].existing_given_price_currency`)

	const { value: existingGivenPrice } = useFieldValue<
		IGiveQuoteData["options"][number]["existing_given_price"]
	>(`options[${optionIndex}].existing_given_price`)

	const existingPackagePriceMinusNewPackagePrice = useMemo(() => {
		const existingGivenPriceMoney =
			existingGivenPriceCurrency && existingGivenPrice
				? moneyParseByDecimal(
						Number(existingGivenPrice || "0"),
						existingGivenPriceCurrency
					)
				: undefined

		if (
			!existingGivenPriceMoney ||
			existingGivenPriceCurrency !== givenPriceCurrency
		) {
			return false
		}

		const newGivenPriceMoney = moneyParseByDecimal(
			packagePriceInGivenCurrency || 0,
			givenPriceCurrency || "INR"
		)

		return subtractMoney(existingGivenPriceMoney, newGivenPriceMoney)
	}, [
		existingGivenPriceCurrency,
		existingGivenPrice,
		packagePriceInGivenCurrency,
		givenPriceCurrency,
	])
	const chargeAblePax =
		noOfAdults + (children?.length || 0) - complimentaryCount
	const perPersonPrice = roundMoney(
		divideMoneyBy(
			moneyParseByDecimal(packagePriceInGivenCurrency, givenPriceCurrency),
			chargeAblePax,
			"ceil"
		),
		"ceil"
	)
	const perPersonPriceFormatted = formatMoneyByIntl(perPersonPrice, {
		showSolidus: true,
	})
	const perPersonPriceStirng = `${perPersonPriceFormatted} Per Person X ${chargeAblePax} Pax + ${complimentaryCount} FOC`

	const hasFlightGivenPricesPerPerson = (givenPricesPerPerson || []).some(
		(a) => a.service === "flight"
	)

	return (
		<Stack gap="4" maxWidth="3xl">
			{givenPricesPerPerson &&
			(pricingStrategy === "per_person_v1" ||
				pricingStrategy === "per_person_v2" ||
				pricingStrategy === "per_component_per_person_v2") ? (
				<Table
					bordered
					headers={["", "Per Pax", "Qnty.", "Sub Total"]}
					responsive
					alignCols={{ 1: "right", 2: "right", 3: "right" }}
				>
					<tbody>
						{(givenPricesPerPerson || []).map(
							({
								id,
								price,
								currency,
								per_pax_label,
								total_pax_label,
								pax_count,
							}) => (
								<tr key={`${id}-${total_pax_label}-${price}`}>
									<TableDataCell>
										<Text>
											{per_pax_label} x {total_pax_label}{" "}
										</Text>
									</TableDataCell>
									<TableDataCell textAlign="right" fontWeight="semibold">
										<Money currency={currency} amount={Number(price)} />
									</TableDataCell>
									<TableDataCell textAlign="right">{pax_count}</TableDataCell>
									<TableDataCell textAlign="right">
										<Money
											currency={currency}
											amount={Number(price) * pax_count}
										/>
									</TableDataCell>
								</tr>
							)
						)}
						{flightResults.roundedInGiven.amount > 0 &&
						!hasFlightGivenPricesPerPerson ? (
							<tr>
								<TableDataCell colSpan={3}>Flights</TableDataCell>
								<TableDataCell textAlign="right" fontWeight="semibold">
									<Money money={flightResults.roundedInGiven} />
								</TableDataCell>
							</tr>
						) : null}
					</tbody>
					<tfoot>
						<tr>
							<TableFooterDataCell colSpan={3} textAlign="right">
								<Stack gap="1">
									<Text fontWeight="semibold" fontSize="lg">
										Total
									</Text>
									{pricingStrategy === "per_person_v1" ||
									pricingStrategy === "per_person_v2" ? ( //TODO - Currently showing tax percentage for old strategies only, to do later - show tax percentage for old and for new show only if editing total for tax is true
										<Text>Tax: {taxName} Included</Text>
									) : null}
								</Stack>
							</TableFooterDataCell>
							<TableFooterDataCell textAlign="right">
								<Text fontSize="lg" fontWeight="semibold" textAlign="right">
									<Money
										currency={givenPriceCurrency}
										amount={packagePriceInGivenCurrency}
										showCurrency
									/>
								</Text>
							</TableFooterDataCell>
						</tr>
					</tfoot>
				</Table>
			) : (
				<Stack gap="2">
					<Text fontSize="3xl" fontWeight="semibold">
						<Money
							currency={givenPriceCurrency}
							amount={packagePriceInGivenCurrency}
							showCurrency
						/>
					</Text>
					{(pricingStrategy === "overall_v1" ||
						pricingStrategy === "overall_v2") &&
					complimentaryCount > 0 ? (
						<Text fontWeight="semibold">{`${perPersonPriceStirng}`}</Text>
					) : null}
					{pricingStrategy === "overall_v1" ||
					pricingStrategy === "overall_v2" ||
					pricingStrategy === "per_person_v1" ||
					pricingStrategy === "per_person_v2" ? ( //TODO - Currently showing tax percentage for old statgies only, to do later - show tax percentage for old and for new show only if editing total for tax is true
						<Text fontWeight="semibold">{taxName}: Included </Text>
					) : null}
				</Stack>
			)}
			{existingPackagePriceMinusNewPackagePrice &&
			existingPackagePriceMinusNewPackagePrice.amount > 0 ? (
				<Alert status="warning" title="Lower Package Price than Existing One">
					The new package price is{" "}
					<Money
						money={existingPackagePriceMinusNewPackagePrice}
						showCurrency
					/>{" "}
					lower than existing one (
					<Money
						currency={existingGivenPriceCurrency || givenPriceCurrency}
						amount={existingGivenPrice || 0}
						showCurrency
					/>
					).
				</Alert>
			) : null}
		</Stack>
	)
}

function SyncPackagePrice({
	optionIndex,
	hasPerPersonCostPrices,
	costPriceWithoutFlights,
	flightPriceInCostingCurrency,
	perPersonCostPriceAllocation,
	flightCostPriceInCostingCurrency,
	enableFlightRounding,
}: {
	optionIndex: number
	hasPerPersonCostPrices: boolean
	costPriceWithoutFlights: number
	flightPriceInCostingCurrency: number
	perPersonCostPriceAllocation: ITripQuote["cost_prices_per_person"]
	flightCostPriceInCostingCurrency?: number
	enableFlightRounding?: boolean
}) {
	const { value: gstIncluded } = useFieldValue<boolean>(
		"options[0].gst_included"
	)
	const { value: tax_percentage } = useFieldValue<number | null | undefined>(
		"options[0].taxes.total_percentage"
	)
	const { value: globalTaxAppliedOn } = useFieldValue<
		TTaxAppliedOn | undefined
	>("options[0].taxes.total_applied_on")
	const { value: givenPricesPerPerson } = useFieldValue<
		IGiveQuoteData["options"][number]["given_prices_per_person"]
	>(`options[${optionIndex}].given_prices_per_person`)
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { value: enableSyncRoundingAmongOptions } = useFieldValue<
		0 | 1 | undefined
	>("enable_sync_rounding_among_options")

	const { value: option1MarginsRoundingPoint } = useFieldValue<
		number | undefined
	>(`options[0].margins.rounding_point`)

	const { value: margins } = useFieldValue<IGivenQuote["margins"]>(
		`options[${optionIndex}].margins`
	)

	const totalCalculations = useTotalCalculationsForV1Strategies({
		optionIndex,
		hasPerPersonCostPrices,
		costPriceWithoutFlights,
		flightPriceInCostingCurrency,
		flightCostPriceInCostingCurrency,
		enableFlightRounding: Boolean(enableFlightRounding),
	})

	const form = useForm()

	const roundingPoint = enableSyncRoundingAmongOptions
		? option1MarginsRoundingPoint
		: margins?.rounding_point

	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			givenPriceCurrency
		)

	const tax =
		isTruthy(gstIncluded) && Number(tax_percentage || 0)
			? Number(tax_percentage || 0)
			: 0

	// Flights Cost allocations
	const { flight: flightAllocations = [] } = useMemo(
		() => partitionPerPersonAllocations(perPersonCostPriceAllocation || []),
		[perPersonCostPriceAllocation]
	)
	const { allocationResults: flightAllocationResults } =
		useFlightAllocationsPricing({
			optionIndex,
			costPriceCurrency,
			givenPriceCurrency,
			flightCostPriceInCostingCurrency: Number(
				flightCostPriceInCostingCurrency || 0
			),
			flightPriceInCostingCurrency: Number(flightPriceInCostingCurrency || 0),
			flightAllocations,
			enableFlightRounding: Boolean(enableFlightRounding),
		})

	// update the per-person given prices (accommodation + flight)
	useEffect(() => {
		// No per-person cost prices — clear per-person given prices so the
		// form falls back to total-based pricing (handled by the effect below).
		if (!hasPerPersonCostPrices) {
			const h = setTimeout(() => {
				form.change(
					`options[${optionIndex}].given_prices_per_person`,
					undefined
				)

				form.change(
					`options[${optionIndex}].unrounded_given_prices.per_person_allocation`,
					undefined
				)
			}, 100)
			return () => clearTimeout(h)
		}

		// Hotel / transport / activity entries: apply accommodation markup + global tax ------- flights excluded
		const accommodationGivenPrices = (perPersonCostPriceAllocation || [])
			.filter(
				(allocation) =>
					moneyParseByDecimal(Number(allocation.price), costPriceCurrency)
						.amount > 0 && allocation.service !== "flight"
			)
			.map((allocation) => {
				const perPersonCostPrice = moneyParseByDecimal(
					Number(allocation.price || 0),
					costPriceCurrency
				)

				const perPersonMarkup = moneyParseByDecimal(
					Number(margins?.per_person_allocation?.[allocation.id] || 0),
					costPriceCurrency
				)

				const perPersonCostPriceWithMarkup = addMoney(
					perPersonCostPrice,
					perPersonMarkup
				)

				let perPersonCostPriceWithMarkupWithTax = perPersonCostPriceWithMarkup

				if (tax) {
					let taxBase
					if (globalTaxAppliedOn === "only_markup") {
						// Calculate tax only on markup amount

						taxBase = perPersonMarkup
					} else {
						// Calculate tax on cost + markup

						taxBase = perPersonCostPriceWithMarkup
					}

					const perPersonTax =
						taxBase.amount > 0
							? getPercentageOfMoney(taxBase, Number(tax))
							: createMoney(0, costPriceCurrency)

					perPersonCostPriceWithMarkupWithTax = addMoney(
						perPersonCostPriceWithMarkup,
						perPersonTax
					)
				}

				const givenPrice = convertMoneyToCurrency(
					perPersonCostPriceWithMarkupWithTax,
					givenPriceCurrency,
					Number(costPriceGivenPriceCurrencyPairRatio || 0)
				)

				const unroundedGivenPriceInGivenCurrency = givenPrice
				const roundedGivenPriceInGivenCurrency = roundMoneyToPoint(
					givenPrice,
					roundingPoint
				)
				return {
					...allocation,
					currency: givenPriceCurrency,
					price: formatMoneyByDecimal(roundedGivenPriceInGivenCurrency),
					price_unrounded: formatMoneyByDecimal(
						unroundedGivenPriceInGivenCurrency
					),
				}
			})

		// flight given prices
		const flightGivenPrices = flightAllocations
			.map((allocation, index) => ({
				allocation,
				result: flightAllocationResults[index],
			}))
			.map(({ allocation, result }) => ({
				...allocation,
				currency: givenPriceCurrency,
				price: formatMoneyByDecimal(result.roundedInGiven),
				price_unrounded: formatMoneyByDecimal(result.unroundedInGiven),
				price_in_costing_currency: formatMoneyByDecimal(result.unrounded),
			}))
		const allGivenPrices = [...accommodationGivenPrices, ...flightGivenPrices]

		// - given_prices_per_person (drives the per-pax table display and total given_price)
		const givenPricesPerPersonAfterRounding = allGivenPrices.map((p) => {
			const { price_unrounded, ...rest } = p
			return { ...rest }
		})

		// - unrounded_given_prices.per_person_allocation (used to detect rounding gaps)
		const givenPricesPerPersonUnrounded = allGivenPrices.reduce(
			(acc, person) => ({
				...acc,
				[person.id]: Number(person.price_unrounded || 0),
			}),
			{} as Record<string, number>
		)

		const h = setTimeout(() => {
			form.change(
				`options[${optionIndex}].given_prices_per_person`,
				givenPricesPerPersonAfterRounding
			)

			form.change(
				`options[${optionIndex}].unrounded_given_prices.per_person_allocation`,
				givenPricesPerPersonUnrounded
			)
		}, 100)
		return () => clearTimeout(h)
	}, [
		optionIndex,
		form,
		perPersonCostPriceAllocation,
		flightAllocations,
		flightAllocationResults,
		margins,
		tax,
		costPriceGivenPriceCurrencyPairRatio,
		costPriceCurrency,
		givenPriceCurrency,
		roundingPoint,
		globalTaxAppliedOn,
		hasPerPersonCostPrices,
	])

	const hasPerPersonPricingEnabled =
		givenPricesPerPerson?.length && hasPerPersonCostPrices

	// calculate given price using per-person: sum all per-pax prices × pax_count
	// (accommodation + flight entries are both in given_prices_per_person)
	useEffect(() => {
		if (hasPerPersonPricingEnabled) {
			const perPersonGivenPrice = moneyParseByDecimal(
				givenPricesPerPerson.reduce<number>(
					(total, { price, pax_count }) => total + Number(price) * pax_count,
					0
				),
				givenPriceCurrency
			)
			// this is because we have to handle old quotes before flight per person update
			// when there is flight but no flight per person in per person startegy
			// so total given price will sum given_prices_per_person and used directly
			//  only when flight allocations are available , when flight allocations are not available
			// then given_prices_pers_person + flight aggregate rounded givenPrice = total Given Price
			const totalGivenPrice = flightAllocations.length
				? perPersonGivenPrice
				: addMoney(
						perPersonGivenPrice,
						totalCalculations.flights.roundedInGivenCurrency
					)
			const h = setTimeout(() => {
				form.change(
					`options[${optionIndex}].given_price`,
					formatMoneyByDecimal(totalGivenPrice)
				)
			}, 100)
			return () => clearTimeout(h)
		}
	}, [
		optionIndex,
		givenPricesPerPerson,
		form,
		givenPriceCurrency,
		hasPerPersonPricingEnabled,
		flightAllocations.length,
		totalCalculations.flights.roundedInGivenCurrency,
	])

	useEffect(() => {
		if (!hasPerPersonPricingEnabled) {
			const h = setTimeout(() => {
				form.change(
					`options[${optionIndex}].given_price`,
					formatMoneyByDecimal(totalCalculations.total.roundedInGivenCurrency)
				)
			}, 100)
			return () => clearTimeout(h)
		}
	}, [
		optionIndex,
		hasPerPersonPricingEnabled,
		costPriceWithoutFlights,
		flightPriceInCostingCurrency,
		totalCalculations.total.roundedInGivenCurrency,
		form,
	])

	return null
}

/**
 *
 * This component is used to sync the calculated values for the old pricing
 * strategies to the form state which are needed for the API payload
 */
function SyncCalculatedValuesForStrategiesV1({
	optionIndex,
	hasPerPersonCostPrices,
	quoteComponentPrices,
	perPersonCostPriceAllocation,
	enableFlightRounding = false,
}: {
	optionIndex: number
	hasPerPersonCostPrices: boolean
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	perPersonCostPriceAllocation: ITripQuote["cost_prices_per_person"]
	enableFlightRounding?: boolean
}) {
	const form = useForm()
	const {
		totalPrice: totalPriceInCostingCurrency, //total cost price with flight price(flight_given_price) in costing currency,
		flightPrice: flightGivenPriceInCostingCurrency, //flight_given_price(given price of flight) in costing currency,
		flightCostPrice: flightCostPriceInCostingCurrency, //flight_calculated_price(cost price of flight) in costing currency,
		totalHotelPrice: totalHotelPriceInCostingCurrency,
		totalCabsOnlyPrice: totalCabsOnlyPriceInCostingCurrency,
		totalTravelActivitiesOnlyPrice:
			totalTravelActivitiesOnlyPriceInCostingCurrency,
		totalExtrasPrice: totalExtrasPriceInCostingCurrency,
	} = quoteComponentPrices

	const totalCostPriceWithoutFlightPrice = useMemo(() => {
		return subtractMoney(
			totalPriceInCostingCurrency,
			flightGivenPriceInCostingCurrency
		)
	}, [totalPriceInCostingCurrency, flightGivenPriceInCostingCurrency])

	const totalCalculations = useTotalCalculationsForV1Strategies({
		optionIndex,
		hasPerPersonCostPrices,
		costPriceWithoutFlights: Number(
			formatMoneyByDecimal(totalCostPriceWithoutFlightPrice) || 0
		),
		flightPriceInCostingCurrency: Number(
			formatMoneyByDecimal(flightGivenPriceInCostingCurrency) || 0
		),
		flightCostPriceInCostingCurrency: Number(
			formatMoneyByDecimal(flightCostPriceInCostingCurrency) || 0
		),
		enableFlightRounding,
	})

	const perComponentResults = useMemo(() => {
		return {
			hotels: {
				cost_price: Number(
					formatMoneyByDecimal(totalHotelPriceInCostingCurrency) || 0
				),
			},
			cabs: {
				cost_price: Number(
					formatMoneyByDecimal(totalCabsOnlyPriceInCostingCurrency) || 0
				),
			},
			activities: {
				cost_price: Number(
					formatMoneyByDecimal(
						totalTravelActivitiesOnlyPriceInCostingCurrency
					) || 0
				),
			},
			extras: {
				cost_price: Number(
					formatMoneyByDecimal(totalExtrasPriceInCostingCurrency) || 0
				),
			},
			flights: {
				cost_price: Number(
					formatMoneyByDecimal(flightCostPriceInCostingCurrency) || 0
				),
			},
		}
	}, [
		flightCostPriceInCostingCurrency,
		totalHotelPriceInCostingCurrency,
		totalCabsOnlyPriceInCostingCurrency,
		totalTravelActivitiesOnlyPriceInCostingCurrency,
		totalExtrasPriceInCostingCurrency,
	])

	const totalPerComponentCostPriceComponentsTotal = useMemo(() => {
		return Number(
			formatMoneyByDecimal(totalCalculations.subtotal.costPrice) || 0
		)
	}, [totalCalculations.subtotal.costPrice])

	const totalPerPersonCosPriceComponentsPriceInCostingCurrency = useMemo(() => {
		return totalPerComponentCostPriceComponentsTotal
	}, [totalPerComponentCostPriceComponentsTotal])

	useEffect(() => {
		//use for overall v1/v2 stratgies
		const h = setTimeout(() => {
			form.batch(() => {
				const state = form.getState()

				// --- PER-COMPONENT: cost_prices_per_component ---

				const newCostPricesPerComponent = Object.entries(
					perComponentResults
				).map(([key, val]) => ({
					id: key,
					price: val.cost_price,
				}))

				const currentCostPricesPerComponent =
					state.values.options?.[optionIndex]?.cost_prices_per_component || []
				if (
					!isDeepEqual(currentCostPricesPerComponent, newCostPricesPerComponent)
				) {
					form.change(
						`options[${optionIndex}].cost_prices_per_component`,
						newCostPricesPerComponent
					)
				}

				// --- per_component_cost_price_components - TOTAl ---
				const currentPerComponentCostPriceComponentsTotal =
					state.values.options?.[optionIndex]
						?.per_component_cost_price_components?.total

				if (
					currentPerComponentCostPriceComponentsTotal !==
					totalPerComponentCostPriceComponentsTotal
				) {
					form.change(
						`options[${optionIndex}].per_component_cost_price_components.total`,
						totalPerComponentCostPriceComponentsTotal
					)
				}

				// --- PER-COMPONENT: given_prices_per_component (override flights only) ---
				{
					const currentGivenPricesPerComponent =
						state.values.options?.[optionIndex]?.given_prices_per_component ||
						[]

					const currentFlights = currentGivenPricesPerComponent.find(
						(c: { id: string; price: number }) => c.id === "flights"
					)
					const newFlightsPrice = Number(
						formatMoneyByDecimal(
							totalCalculations.flights.roundedInGivenCurrency
						) || 0
					)

					let nextGivenPricesPerComponent = currentGivenPricesPerComponent
					if (!currentFlights) {
						nextGivenPricesPerComponent = [
							...currentGivenPricesPerComponent,
							{ id: "flights", price: newFlightsPrice },
						]
					} else if (currentFlights.price !== newFlightsPrice) {
						nextGivenPricesPerComponent = currentGivenPricesPerComponent.map(
							(c: { id: string; price: number }) =>
								c.id === "flights" ? { ...c, price: newFlightsPrice } : c
						)
					}

					if (
						!isDeepEqual(
							currentGivenPricesPerComponent,
							nextGivenPricesPerComponent
						)
					) {
						form.change(
							`options[${optionIndex}].given_prices_per_component`,
							nextGivenPricesPerComponent
						)
					}
				}

				// --- Taxes: per-component allocation (override flights only) ---
				const currentTaxesPerComponentAllocation =
					state.values.options?.[optionIndex]?.taxes
						?.per_component_allocation || {}

				const newTaxesPerComponentAllocation = {
					...currentTaxesPerComponentAllocation,
					flights: Number(
						formatMoneyByDecimal(totalCalculations.flights.tax) || 0
					),
				}

				//For v1 pricing strategies there is no option for user to add different tax per compoennt, that'why all other componenets will contains default initialized form state values as Zero Values,
				//But for flights we will only overide and set the state if the flights tax, percentage changed otherwise there is no need to change form state
				if (
					currentTaxesPerComponentAllocation.flights !==
						newTaxesPerComponentAllocation.flights ||
					currentTaxesPerComponentAllocation.flights_percentage !==
						newTaxesPerComponentAllocation.flights_percentage
				) {
					form.change(
						`options[${optionIndex}].taxes.per_component_allocation`,
						newTaxesPerComponentAllocation
					)
				}

				// --- MARKUP: per-component allocation (override flights only) ---

				const currentPerComponentAllocation =
					state.values.options?.[optionIndex]?.margins
						?.per_component_allocation || {}

				const newPerComponentAllocation = {
					...currentPerComponentAllocation,
					flights: Number(
						formatMoneyByDecimal(totalCalculations.flights.markup) || 0
					),
					flights_percentage: totalCalculations.flights.marginPercentage || 0,
				}

				//For v1 pricing strategies there is no option for user to add different markup per compoennt, that'why all other componenets will contains default initialized form state values as Zero Values,
				//But for flights we will only overide and set the state if the flights markup, percentage changed due to flight tax percentage change otherwise there is no need to change form state
				if (
					currentPerComponentAllocation.flights !==
						newPerComponentAllocation.flights ||
					currentPerComponentAllocation.flights_percentage !==
						newPerComponentAllocation.flights_percentage
				) {
					form.change(
						`options[${optionIndex}].margins.per_component_allocation`,
						newPerComponentAllocation
					)
				}

				// -- Total Tax Amount --

				const currentTaxTotal =
					state.values.options?.[optionIndex]?.taxes?.total
				const newTaxTotal = Number(
					formatMoneyByDecimal(totalCalculations.subtotal.tax)
				)
				if (
					typeof newTaxTotal === "number" &&
					!isNaN(newTaxTotal) &&
					currentTaxTotal !== newTaxTotal
				) {
					form.change(`options[${optionIndex}].taxes.total`, newTaxTotal)
				}

				// -- Total Unrounded Given Price in Given Currency --

				const currentTotalUnroundedInGivenCurrency =
					state.values.options?.[optionIndex]?.unrounded_given_prices?.total

				const newTotalUnroundedGivenCurrency = Number(
					formatMoneyByDecimal(
						totalCalculations.total.unroundedInGivenCurrency
					) || 0
				)

				if (
					typeof newTotalUnroundedGivenCurrency === "number" &&
					!isNaN(newTotalUnroundedGivenCurrency) &&
					currentTotalUnroundedInGivenCurrency !==
						newTotalUnroundedGivenCurrency
				) {
					form.change(
						`options[${optionIndex}].unrounded_given_prices.total`,
						newTotalUnroundedGivenCurrency
					)
				}

				// --- PER-COMPONENT: unrounded_given_prices.per_component_allocation (override flights only) ---
				{
					const currentUnroundedPerComponentAllocation =
						state.values.options?.[optionIndex]?.unrounded_given_prices
							?.per_component_allocation || {}

					const newUnroundedPerComponentAllocation = {
						...currentUnroundedPerComponentAllocation,
						flights: Number(
							formatMoneyByDecimal(
								totalCalculations.flights.unroundedInGivenCurrency
							) || 0
						),
					}

					if (
						currentUnroundedPerComponentAllocation.flights !==
						newUnroundedPerComponentAllocation.flights
					) {
						form.change(
							`options[${optionIndex}].unrounded_given_prices.per_component_allocation`,
							newUnroundedPerComponentAllocation
						)
					}
				}

				// -- Subtotal Unrounded Given Price in Given Currency --

				const currentSubtotalUnroundedInGivenCurrency =
					state.values.options?.[optionIndex]?.unrounded_given_prices?.subtotal

				const newSubtotalUnroundedGivenCurrency = Number(
					formatMoneyByDecimal(
						totalCalculations.subtotal.unroundedInGivenCurrency
					) || 0
				)

				if (
					typeof newSubtotalUnroundedGivenCurrency === "number" &&
					!isNaN(newSubtotalUnroundedGivenCurrency) &&
					currentSubtotalUnroundedInGivenCurrency !==
						newSubtotalUnroundedGivenCurrency
				) {
					form.change(
						`options[${optionIndex}].unrounded_given_prices.subtotal`,
						newSubtotalUnroundedGivenCurrency
					)
				}
			})
		}, 100)

		//use for per-person v1/v2 stratgies
		const hPerPerson = setTimeout(() => {
			if (hasPerPersonCostPrices) {
				form.batch(() => {
					const state = form.getState()

					// -- PER PERSON COST PRICE COMPONENTS TOTAL in COSTING CURRENCY ---

					const currentPerPersonCostPriceComponents =
						state.values.options?.[optionIndex]
							?.per_person_cost_price_components

					const newPerPersonCostPriceComponents = {
						...currentPerPersonCostPriceComponents,
						total: totalPerPersonCosPriceComponentsPriceInCostingCurrency,
					}

					if (
						!isDeepEqual(
							currentPerPersonCostPriceComponents,
							newPerPersonCostPriceComponents
						)
					) {
						form.change(
							`options[${optionIndex}].per_person_cost_price_components`,
							newPerPersonCostPriceComponents
						)
					}
				})
			}
		}, 100)

		return () => {
			clearTimeout(h)
			clearTimeout(hPerPerson)
		}
	}, [
		optionIndex,
		form,
		totalCalculations.total.tax,
		totalCalculations.total.unroundedInGivenCurrency,
		totalCalculations.subtotal.tax,
		totalCalculations.subtotal.unroundedInGivenCurrency,
		totalCalculations.flights.tax,
		totalCalculations.flights.taxPercentage,
		totalCalculations.flights.markup,
		totalCalculations.flights.marginPercentage,
		totalCalculations.flights.roundedInGivenCurrency,
		totalCalculations.flights.unroundedInGivenCurrency,
		totalPerComponentCostPriceComponentsTotal,
		perComponentResults,
		totalPerPersonCosPriceComponentsPriceInCostingCurrency,
		perPersonCostPriceAllocation,
		hasPerPersonCostPrices,
	])

	return null
}

export function GivenPackagePriceDetailsInDialog({
	children,
	...props
}: {
	children: (props: { show: () => void }) => React.ReactNode
} & React.ComponentProps<typeof GivenPackagePriceDetails>) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const { tripQuotes } = props

	const firstQuote = tripQuotes[0]

	const isAdvancePricingStrategy =
		firstQuote.pricing_strategy === "per_component_per_person_v2" ||
		firstQuote.pricing_strategy === "per_component_v2" ||
		firstQuote.pricing_strategy === "per_person_v2"

	return (
		<>
			{children({ show: openDialog })}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title="Package Price Details"
				fitContainer={isAdvancePricingStrategy ? true : false}
				lg
			>
				<Dialog.Body>
					<GivenPackagePriceDetails {...props} tripQuotes={tripQuotes} />
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={closeDialog}>Close</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function GivenPackagePriceDetails({
	tripQuotes,
	onChange,
}: {
	tripQuotes: Array<ITripQuote>
	onChange: () => Promise<unknown>
}) {
	const firstQuote = tripQuotes[0]
	const isAdvancePricingStrategy =
		firstQuote.pricing_strategy === "per_component_per_person_v2" ||
		firstQuote.pricing_strategy === "per_component_v2" ||
		firstQuote.pricing_strategy === "per_person_v2"
	// we are using GiveQuoteForm to show the details incase of `perComponentPricing`,
	// and because it is a form, we need to reset its state if we ever fetch the
	// initial/quote details again (e.g. during payment gateway link generation).
	const [keyCounter, setKeyCounter] = useState(0)
	return isAdvancePricingStrategy ? (
		<GiveQuote
			tripQuote={firstQuote}
			readOnly
			key={keyCounter}
			onRefetchInitialValues={() => {
				onChange().then(() => setKeyCounter((count) => count + 1))
			}}
		/>
	) : (
		<Stack gap="6" position="relative" zIndex="0">
			{tripQuotes.map((tripQuote, index, arr) =>
				tripQuote.given_quote ? (
					<Stack
						key={tripQuote.id}
						gap="4"
						position="relative"
						style={{ zIndex: -index }}
					>
						{arr.length > 1 ? (
							<Box position="sticky" top="0" zIndex="10">
								<Box
									position="sticky"
									top="0"
									zIndex="10"
									bgColor="emphasis"
									color="on_emphasis"
									padding="2"
									rounded="md"
								>
									<Heading fontSize="md">
										Option {index + 1}: {tripQuote.name}
									</Heading>
								</Box>
							</Box>
						) : null}
						<Box maxWidth="full">
							<GivenPackageOptionPriceDetails
								tripQuote={tripQuote as Required<ITripQuote, "given_quote">}
								onChange={onChange}
							/>
						</Box>
					</Stack>
				) : null
			)}
		</Stack>
	)
}

function GivenPackageOptionPriceDetails({
	tripQuote,
	onChange,
}: {
	tripQuote: Required<ITripQuote, "given_quote">
	onChange: () => void
}) {
	const totalCostPrice = tripQuote.cost_price
	const givenQuote = tripQuote.given_quote
	const globalTaxAppliedOn =
		givenQuote.taxes?.total_applied_on || "cost_plus_markup" //This component is mainly used for old pricing strategies, so we can assume that tax is applied on cost plus markup by default

	const perPersonCostPriceAllocation = tripQuote.cost_prices_per_person
	const currencyPairs = useMemo(
		() => givenQuote.currency_pairs || tripQuote.quote?.currency_pairs || [],
		[givenQuote, tripQuote]
	)
	const flightGivenPriceInCostingCurrency = useMemo(() => {
		if (!tripQuote) return 0
		return Number(
			formatMoneyByDecimal(
				makeCurrencyConverter(currencyPairs)(
					tripQuote.currency,
					getTotalPriceForExtras(
						tripQuote.quote?.flights.map((f) => ({
							...f,
							price: f.given_price,
						}))
					)
				)
			)
		)
	}, [tripQuote, currencyPairs])

	const flightCostPriceInCostingCurrency = useMemo(() => {
		if (!tripQuote) return 0
		return Number(
			formatMoneyByDecimal(
				makeCurrencyConverter(currencyPairs)(
					tripQuote.currency,
					getTotalPriceForExtras(
						tripQuote.quote?.flights.map((f) => ({
							...f,
							price: f.calculated_price,
						}))
					)
				)
			)
		)
	}, [tripQuote, currencyPairs])

	const {
		currency: costPriceCurrency,
		gst_included,
		comments,
		customer_remarks,
		margins,
		tax_name,
	} = givenQuote

	const costPriceWithoutFlights =
		Number(totalCostPrice) - Number(flightCostPriceInCostingCurrency || 0)

	const totalMarkup = Number(margins?.total || 0)

	const taxPercentage =
		isTruthy(givenQuote.gst_included) && givenQuote.tax_percentage
			? Number(givenQuote.tax_percentage)
			: 0
	let taxBase = costPriceWithoutFlights + totalMarkup

	if (globalTaxAppliedOn === "only_markup") {
		taxBase = totalMarkup
	}

	const totalTax = (taxBase * Number(taxPercentage)) / 100

	const totalWithMarkupTax = costPriceWithoutFlights + totalMarkup + totalTax

	return (
		<Stack>
			<Table bordered hover responsive>
				<thead>
					<tr>
						<TableHeaderDataCell></TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset" textAlign="right">
							Cost ({costPriceCurrency})
						</TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset">
							<Inline gap="1" alignItems="center" justifyContent="center">
								<Text>Markup</Text>
								{isTruthy(margins?.use_percentage) ? (
									<Text>({margins?.total_percentage}%)</Text>
								) : null}
							</Inline>
						</TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset">
							<Inline gap="1" alignItems="center" justifyContent="center">
								<Text>{tax_name}</Text>
								{isTruthy(gst_included) ? (
									<Text>({taxPercentage ? `${taxPercentage}%` : "inc"})</Text>
								) : (
									<Text>(exc)</Text>
								)}
							</Inline>
						</TableHeaderDataCell>
						<TableHeaderDataCell bgColor="inset" textAlign="right">
							Total ({costPriceCurrency})
						</TableHeaderDataCell>
					</tr>
				</thead>
				{perPersonCostPriceAllocation?.length ? (
					<tbody>
						{perPersonCostPriceAllocation.map(
							({ id, per_pax_label, total_pax_label, price: costPrice }) => {
								if (!Number(costPrice)) {
									return null
								}
								const margin = margins?.per_person_allocation?.[id]
								const priceWithMargin = Number(costPrice) + Number(margin || 0)

								let taxBase = priceWithMargin

								if (globalTaxAppliedOn === "only_markup") {
									taxBase = Number(margin || 0)
								}

								const tax = (taxBase * Number(taxPercentage)) / 100
								const total = priceWithMargin + tax
								return (
									<tr key={`${id}-${total_pax_label}-${costPrice}`}>
										<TableDataCell bgColor="inset" fontWeight="semibold">
											<Stack>
												<Text>/{per_pax_label}</Text>
												<Text fontSize="sm" color="muted">
													{total_pax_label}
												</Text>
											</Stack>
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Money
												currency={costPriceCurrency}
												amount={Number(costPrice)}
											/>
										</TableDataCell>
										<TableDataCell textAlign="center">
											{margin ? (
												<Stack alignItems="center">
													<Money currency={costPriceCurrency} amount={margin} />
												</Stack>
											) : (
												"-"
											)}
										</TableDataCell>
										<TableDataCell textAlign="center">
											<Text>
												{tax ? (
													<Money currency={costPriceCurrency} amount={tax} />
												) : (
													"-"
												)}
											</Text>
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Text>
												<Money currency={costPriceCurrency} amount={total} />
											</Text>
										</TableDataCell>
									</tr>
								)
							}
						)}
					</tbody>
				) : null}
				<tbody>
					<tr>
						<TableDataCell bgColor="inset" fontWeight="semibold">
							Total
						</TableDataCell>
						<TableDataCell textAlign="right" fontWeight="semibold">
							<Money
								currency={costPriceCurrency}
								amount={costPriceWithoutFlights}
							/>
						</TableDataCell>
						<TableDataCell textAlign="center" fontWeight="semibold">
							{margins?.total ? (
								<Inline alignItems="center" justifyContent="center" gap="1">
									{!isTruthy(margins?.editing_total) &&
									perPersonCostPriceAllocation?.length ? (
										<Tooltip content="Total Markup from Per-Pax Allocation">
											<Box
												fontSize="sm"
												color="muted"
												fontWeight="normal"
												paddingX="1"
											>
												=
											</Box>
										</Tooltip>
									) : null}
									<Money currency={costPriceCurrency} amount={margins.total} />
								</Inline>
							) : (
								"-"
							)}
						</TableDataCell>
						<TableDataCell textAlign="center" fontWeight="semibold">
							{taxPercentage ? (
								<Money currency={costPriceCurrency} amount={totalTax} />
							) : (
								"-"
							)}
						</TableDataCell>
						<TableDataCell textAlign="right" fontWeight="semibold">
							<Money currency={costPriceCurrency} amount={totalWithMarkupTax} />
						</TableDataCell>
					</tr>
				</tbody>
				{flightGivenPriceInCostingCurrency ? (
					<tfoot>
						<tr>
							<TableFooterDataCell colSpan={5}>
								<Inline gap="4" justifyContent="end" alignItems="center">
									<Box>
										<Icons.Airplane /> Flights:
									</Box>
									<Box>
										<Money
											currency={costPriceCurrency}
											amount={flightGivenPriceInCostingCurrency}
										/>
									</Box>
								</Inline>
							</TableFooterDataCell>
						</tr>
					</tfoot>
				) : null}
			</Table>
			<Stack marginY="4" gap="4">
				<DisplayGivenQuotePricing
					givenQuote={tripQuote.given_quote}
					onChange={onChange}
				/>
				<DisplayGivenQuoteInternalCommentsAndCustomerRemarks
					internalComments={comments}
					customerRemarks={customer_remarks}
				/>
			</Stack>
		</Stack>
	)
}

export function DisplayGivenQuotePricing({
	givenQuote,
	onChange,
}: {
	givenQuote: Pick<
		IGivenQuote,
		| "id"
		| "margins"
		| "given_currency"
		| "given_price"
		| "tax_label"
		| "tax_name"
		| "gst_included"
		| "tax_percentage"
		| "currency_pairs"
		| "given_prices_per_person"
		| "currency"
	>
	onChange: () => void
}) {
	const {
		margins,
		given_currency,
		given_price,
		tax_label,
		tax_name,
		gst_included,
		tax_percentage,
		currency_pairs,
		given_prices_per_person,
		currency: costPriceCurrency,
	} = givenQuote

	const costPriceGivenPriceCurrencyPair = useMemo(() => {
		const currencyPairIndex = currency_pairs.findIndex(
			(pair) =>
				pair.baseCurrency === costPriceCurrency &&
				pair.counterCurrency === given_currency
		)
		return currency_pairs.at(currencyPairIndex)
	}, [currency_pairs, costPriceCurrency, given_currency])

	const pgIntegratedAppsCount = usePaymentGatewayIntegratedAppsCount()
	return (
		<Stack
			borderLeftWidth="4"
			borderColor="success"
			marginY="4"
			bgColor="success"
			padding="4"
		>
			{margins?.rounding_point ? (
				<Inline
					gap="1"
					alignItems="center"
					fontWeight="semibold"
					color="accent"
				>
					<Text>Rounding:</Text>
					<Text>{margins?.rounding_point}</Text>
				</Inline>
			) : null}
			<Inline flexWrap="wrap" gap="12">
				<Inline gap="12" flexWrap="wrap">
					<Stack gap="1">
						<Inline gap="4">
							<Text fontWeight="semibold">Quote/Selling Price</Text>
						</Inline>
						<Inline gap="2">
							<Text fontWeight="semibold" fontSize="lg">
								{given_currency}
							</Text>
							<Text fontSize="3xl" fontWeight="semibold">
								<Money currency={given_currency} amount={given_price} />
							</Text>
						</Inline>
						{costPriceCurrency !== given_currency ? (
							<Box>
								{costPriceGivenPriceCurrencyPair
									? currencyPairToHuman(costPriceGivenPriceCurrencyPair)
									: null}
							</Box>
						) : null}
					</Stack>
					{pgIntegratedAppsCount ? (
						<Stack gap="1">
							<Text fontWeight="semibold">Payment Link</Text>
							<GivenQuotePaymentLink
								givenQuote={givenQuote}
								onChange={onChange}
							/>
						</Stack>
					) : null}
				</Inline>
				<Stack gap="1">
					<Text fontWeight="semibold">
						{tax_label ? `${tax_name} (${tax_label})` : `Tax: ${tax_name}`}
					</Text>
					<Text fontWeight="semibold">
						{isTruthy(gst_included)
							? `Included${tax_percentage ? ` (${tax_percentage}%)` : ``}`
							: "Excluded"}{" "}
					</Text>
				</Stack>
			</Inline>

			<GivenPricesPerPersonSharing
				given_prices_per_person={given_prices_per_person ?? []}
			/>
		</Stack>
	)
}

function DisplayGivenQuoteInternalCommentsAndCustomerRemarks({
	internalComments,
	customerRemarks,
}: {
	internalComments: IGivenQuote["comments"]
	customerRemarks: IGivenQuote["customer_remarks"]
}) {
	return (
		<Stack gap="1">
			{internalComments ? (
				<Stack gap="1">
					<Text fontSize="sm" fontWeight="semibold" color="muted">
						Comments
					</Text>

					<Box
						as="blockquote"
						style={{
							fontStyle: "italic",
						}}
					>
						{internalComments}
					</Box>
				</Stack>
			) : null}
			{customerRemarks ? (
				<Stack gap="1">
					<Text fontSize="sm" fontWeight="semibold" color="muted">
						Customer/Agent Remarks
					</Text>

					<Stack
						borderLeftWidth="2"
						borderColor="success"
						paddingLeft="4"
						gap="2"
						color="success"
					>
						<Markdown>{customerRemarks}</Markdown>
					</Stack>
				</Stack>
			) : null}
		</Stack>
	)
}

/**
 * The flights section of the pricing table is made of two distinct things that
 * were previously crammed into one component:
 *
 *  1. `FlightPerPaxAllocationRows` — one row per passenger-type allocation
 *     (adult/child/infant). Only rendered when per-person pricing is enabled.
 *  2. `FlightAggregateRow` — the single "Flights" sub-total row carrying the
 *     editable tax inputs and the rounded grand total.
 *
 * `FlightsComponentRow` just wires the shared flight calculations into both so
 * the per-pax rows and the aggregate row always stay consistent.
 */
function FlightsComponentRow({
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	flightCostPriceInCostingCurrency,
	flightPriceInCostingCurrency,
	enableFlightRounding,
	globalTaxEnabled,
	flightAllocations,
	readOnly,
}: {
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	flightCostPriceInCostingCurrency: number
	flightPriceInCostingCurrency: number
	enableFlightRounding: boolean
	globalTaxEnabled: boolean
	flightAllocations: TPricePerPersonAllocation[]
	readOnly?: boolean
}) {
	const flightResults = useFlightPriceCalculation({
		optionIndex,
		flightCostPrice: flightCostPriceInCostingCurrency,
		flightGivenPrice: flightPriceInCostingCurrency,
		enableRounding: enableFlightRounding,
	})

	const { value: includePerPersonPrice } = useFieldValue<
		INewQuoteState["include_per_person_price"]
	>(`include_per_person_price`)
	const { allocationResults, flightAllocationTotals } =
		useFlightAllocationsPricing({
			optionIndex,
			costPriceCurrency,
			givenPriceCurrency,
			flightCostPriceInCostingCurrency,
			flightPriceInCostingCurrency,
			flightAllocations,
			enableFlightRounding,
		})

	const flightRoundedTotalFromAllocations =
		flightAllocationTotals.roundedInGiven ?? flightResults.roundedInGiven
	return (
		<tbody>
			{includePerPersonPrice ? (
				<FlightPerPaxAllocationRows
					flightAllocations={flightAllocations}
					allocationResults={allocationResults}
					costPriceCurrency={costPriceCurrency}
					givenPriceCurrency={givenPriceCurrency}
					enableFlightRounding={enableFlightRounding}
					globalTaxEnabled={globalTaxEnabled}
				/>
			) : null}
			<FlightAggregateRow
				flightResults={flightResults}
				flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency}
				costPriceCurrency={costPriceCurrency}
				givenPriceCurrency={givenPriceCurrency}
				enableFlightRounding={enableFlightRounding}
				globalTaxEnabled={globalTaxEnabled}
				isPerPersonPricing={Boolean(includePerPersonPrice)}
				flightRoundedTotalFromAllocations={flightRoundedTotalFromAllocations}
				readOnly={readOnly}
			/>
		</tbody>
	)
}

/**
 * One row per passenger-type flight allocation (adult/child/infant), showing the
 * per-pax cost, markup, tax and rounded selling price. Rendered only when
 * per-person pricing is enabled.
 */
function FlightPerPaxAllocationRows({
	flightAllocations,
	allocationResults,
	costPriceCurrency,
	givenPriceCurrency,
	enableFlightRounding,
	globalTaxEnabled,
}: {
	flightAllocations: TPricePerPersonAllocation[]
	allocationResults: ReturnType<
		typeof useFlightAllocationsPricing
	>["allocationResults"]
	costPriceCurrency: string
	givenPriceCurrency: string
	enableFlightRounding: boolean
	globalTaxEnabled: boolean
}) {
	return (
		<>
			{flightAllocations.map((allocation, index) => {
				const result = allocationResults[index]

				return (
					<tr key={allocation.id}>
						<TableDataCell bgColor="inset" fontWeight="semibold">
							<Stack>
								<Text>/{allocation.per_pax_label}</Text>
								<Text fontSize="sm" color="muted">
									{allocation.total_pax_label}
								</Text>
							</Stack>
						</TableDataCell>

						<TableDataCell textAlign="right">
							<Money currency={costPriceCurrency} amount={+allocation.price} />
							<Text color="muted" fontSize="sm">
								x {allocation.pax_count}
							</Text>
						</TableDataCell>

						<TableDataCell textAlign="center">
							<Money money={result.markup} />
						</TableDataCell>

						<TableDataCell textAlign="center">
							{globalTaxEnabled
								? result.taxAppliedOn === "only_markup"
									? "Only Markup"
									: "Cost + Markup"
								: "-"}
						</TableDataCell>

						<TableDataCell textAlign="center">
							<Money money={result.tax} />
						</TableDataCell>

						<TableDataCell textAlign="right">
							<Money money={result.unrounded} />
						</TableDataCell>

						{givenPriceCurrency && givenPriceCurrency !== costPriceCurrency ? (
							<TableDataCell textAlign="right">
								<Money money={result.unroundedInGiven} />
							</TableDataCell>
						) : null}

						<TableDataCell borderLeftWidth="1" textAlign="right">
							<Money
								money={
									enableFlightRounding
										? result.roundedInGiven
										: result.unroundedInGiven
								}
							/>
							<Text fontSize="sm">x {allocation.pax_count}</Text>
						</TableDataCell>
					</tr>
				)
			})}
		</>
	)
}

/**
 * The single aggregate "Flights" row holding the editable tax inputs and the
 * flight grand total. When per-person pricing is on it shows the sub-total label
 * and uses the per-pax summed rounded total; otherwise it rounds the aggregate.
 */
function FlightAggregateRow({
	flightResults,
	flightCostPriceInCostingCurrency,
	costPriceCurrency,
	givenPriceCurrency,
	enableFlightRounding,
	globalTaxEnabled,
	isPerPersonPricing,
	flightRoundedTotalFromAllocations,
	readOnly,
}: {
	flightResults: ReturnType<typeof useFlightPriceCalculation>
	flightCostPriceInCostingCurrency: number
	costPriceCurrency: string
	givenPriceCurrency: string
	enableFlightRounding: boolean
	globalTaxEnabled: boolean
	isPerPersonPricing: boolean
	flightRoundedTotalFromAllocations: TMoney
	readOnly?: boolean
}) {
	return (
		<tr>
			<TableDataCell bgColor="inset" fontWeight="semibold">
				<Inline gap="2" alignItems="center">
					<Icons.Airplane />
					<Text>Flights {isPerPersonPricing ? "(Sub total) " : null}</Text>
				</Inline>
			</TableDataCell>
			<TableDataCell textAlign="right" fontWeight="semibold">
				<Money
					currency={costPriceCurrency}
					amount={flightCostPriceInCostingCurrency}
				/>
			</TableDataCell>
			<TableDataCell textAlign="center">
				<Money money={flightResults.markup} />
			</TableDataCell>
			<TableDataCell textAlign="center" fontWeight="semibold">
				{globalTaxEnabled ? (
					<SelectInputField
						name={`options[0].taxes.per_component_allocation.flights_applied_on`}
						size="sm"
						placeholder="Tax Applied On"
						disabled={readOnly}
					>
						<option value="cost_plus_markup">Cost + Markup</option>
						<option value="only_markup">Only Markup</option>
					</SelectInputField>
				) : (
					"-"
				)}
			</TableDataCell>
			<TableDataCell textAlign="center" fontWeight="semibold">
				{globalTaxEnabled ? (
					<Stack gap="1" alignItems="center">
						<PercentageInputField
							name={`options[0].taxes.per_component_allocation.flights_percentage`}
							size="sm"
							placeholder="Tax %"
							disabled={readOnly}
						/>

						<Inline
							gap="1"
							alignItems="center"
							justifyContent="center"
							fontSize={"sm"}
						>
							= <Money money={flightResults.tax} />
						</Inline>
					</Stack>
				) : (
					"-"
				)}
			</TableDataCell>
			<TableDataCell textAlign="right" fontWeight="semibold">
				<Money money={flightResults.unrounded} showCurrency />
			</TableDataCell>
			{givenPriceCurrency && givenPriceCurrency !== costPriceCurrency ? (
				<TableDataCell textAlign="right" fontWeight="semibold">
					<Money money={flightResults.unroundedInGiven} showCurrency />
				</TableDataCell>
			) : null}
			<TableDataCell
				textAlign="right"
				fontWeight="semibold"
				borderLeftWidth="1"
			>
				<Money
					money={
						enableFlightRounding
							? isPerPersonPricing
								? flightRoundedTotalFromAllocations
								: flightResults.roundedInGiven
							: flightResults.unroundedInGiven
					}
					showCurrency
				/>
			</TableDataCell>
		</tr>
	)
}

export function GivenPricesPerPersonSharing({
	given_prices_per_person,
}: {
	given_prices_per_person: TPricePerPersonAllocation[]
}) {
	const groups = groupPricesPerPersonByService(given_prices_per_person)

	if (!groups.length) return null
	return (
		<Stack gap={"2"}>
			{groups.map(({ service, prices, title, subtotal }) => (
				<Box key={service}>
					<Text fontWeight="semibold" fontSize={"md"}>
						{title}
					</Text>
					<table>
						<tbody>
							{prices.map(
								({ id, price, currency, per_pax_label, total_pax_label }) => (
									<tr key={`${id}-${total_pax_label}-${price}`}>
										<Box as="td" textAlign="right">
											<Money currency={currency} amount={Number(price)} />
										</Box>
										<td>
											&nbsp;/&nbsp;{per_pax_label} x {total_pax_label}
										</td>
									</tr>
								)
							)}
						</tbody>
					</table>
					{groups?.length > 1 && (
						<Inline fontWeight="semibold" gap={"1"} alignItems={"center"}>
							<Text>Subtotal :</Text>
							<Box>
								<Money
									amount={subtotal.amount}
									currency={subtotal.currency}
									showSolidus
								/>
							</Box>
						</Inline>
					)}
				</Box>
			))}
		</Stack>
	)
}
